import React, { useState, useEffect, useRef, useCallback } from 'react';
import SignaturePad from 'react-signature-canvas';
import axios from 'axios';
import { API_ENDPOINTS } from '../../config/api';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './styles/ContractModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagicWandSparkles, faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-hot-toast';

// Custom CSS for line height dropdown
const lineHeightStyles = `
  .ql-lineHeight {
    width: 65px !important;
  }
  .ql-lineHeight .ql-picker-label::before {
    content: "Height";
  }
  .ql-lineHeight .ql-picker-item[data-value="normal"]::before { content: "Normal"; }
  .ql-lineHeight .ql-picker-item[data-value="1.2"]::before { content: "1.2"; }
  .ql-lineHeight .ql-picker-item[data-value="1.5"]::before { content: "1.5"; }
  .ql-lineHeight .ql-picker-item[data-value="1.8"]::before { content: "1.8"; }
  .ql-lineHeight .ql-picker-item[data-value="2.0"]::before { content: "2.0"; }
  .ql-lineHeight .ql-picker-item[data-value="2.5"]::before { content: "2.5"; }
  .ql-lineHeight .ql-picker-item[data-value="3.0"]::before { content: "3.0"; }
`;

// Add styles to document
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = lineHeightStyles;
  document.head.appendChild(styleElement);
}

const SIGNED_STATUS_VALUES = new Set(['fully_executed', 'executed', 'completed', 'signed']);
const PENDING_STATUS_VALUES = new Set([
  'sent_for_signature',
  'sent_to_client',
  'sent',
  'client_signed',
  'rep_signed',
  'pending',
  'awaiting_client',
  'client_signature_pending'
]);

const getStatusMeta = (statusValue) => {
  const normalized = (statusValue || '').toLowerCase();

  if (SIGNED_STATUS_VALUES.has(normalized)) {
    return {
      badgeLabel: '✓ Signed',
      badgeColor: '#10b981',
      isSigned: true,
      rawStatus: normalized || 'fully_executed',
      displayLabel: 'Fully Executed'
    };
  }

  if (PENDING_STATUS_VALUES.has(normalized)) {
    let displayLabel = 'Pending Signature';
    if (normalized === 'client_signed') displayLabel = 'Client Signed';
    if (normalized === 'rep_signed') displayLabel = 'Representative Signed';
    if (normalized === 'sent_for_signature' || normalized === 'sent' || normalized === 'sent_to_client') {
      displayLabel = 'Sent for Signature';
    }

    return {
      badgeLabel: '⏳ Pending',
      badgeColor: '#f59e0b',
      isSigned: false,
      rawStatus: normalized || 'pending',
      displayLabel
    };
  }

  if (normalized === 'approved') {
    return {
      badgeLabel: '✅ Approved',
      badgeColor: '#10b981',
      isSigned: false,
      rawStatus: 'approved',
      displayLabel: 'Approved'
    };
  }

  if (normalized === 'changes_requested') {
    return {
      badgeLabel: '🔄 Changes Requested',
      badgeColor: '#f59e0b',
      isSigned: false,
      rawStatus: 'changes_requested',
      displayLabel: 'Changes Requested'
    };
  }

  if (normalized === 'rejected') {
    return {
      badgeLabel: '❌ Rejected',
      badgeColor: '#dc3545',
      isSigned: false,
      rawStatus: 'rejected',
      displayLabel: 'Rejected'
    };
  }

  if (normalized === 'template') {
    return {
      badgeLabel: '📄 Template',
      badgeColor: '#6b7280',
      isSigned: false,
      rawStatus: 'template',
      displayLabel: 'Template'
    };
  }

  return {
    badgeLabel: '📝 Draft',
    badgeColor: '#6b7280',
    isSigned: false,
    rawStatus: normalized || 'draft',
    displayLabel: 'Draft'
  };
};

const ContractModal = ({ isOpen, onClose, client, onSave }) => {
  const quillRef = useRef();
  const autoSaveTimeout = useRef(null);


  // Register custom line height format
  useEffect(() => {
    if (typeof window !== "undefined") {
      const Quill = require('react-quill').Quill;
      
      // Create new class for line height
      class LineHeightClass {
        constructor(quill, props) {
          this.quill = quill;
          this.toolbar = quill.getModule('toolbar');
          if (typeof this.toolbar !== 'undefined') {
            this.toolbar.addHandler('lineHeight', this.lineHeightHandler.bind(this));
          }
        }

        lineHeightHandler(value) {
          let format = this.quill.getFormat();
          if (value === 'normal') {
            this.quill.format('lineHeight', false);
          } else {
            this.quill.format('lineHeight', value);
          }
        }
      }
      
      // Register the module
      Quill.register('modules/lineHeight', LineHeightClass, true);

      // Register the format
      let Parchment = Quill.import('parchment');
      let lineHeightConfig = {
        scope: Parchment.Scope.INLINE,
        whitelist: ['1.0', '1.2', '1.5', '1.8', '2.0', '2.5', '3.0']
      };
      let lineHeightClass = new Parchment.Attributor.Style('lineHeight', 'line-height', lineHeightConfig);
      Quill.register(lineHeightClass, true);
    }
  }, []);
  // Quill editor modules configuration
  const quillModules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        [{ 'lineHeight': ['normal', '1.2', '1.5', '1.8', '2.0', '2.5', '3.0'] }],
        ['clean']
      ]
    },
    lineHeight: true
  };

  // Custom line height handler
  const handleLineHeight = (value) => {
    const quill = quillRef.current.getEditor();
    quill.format('lineHeight', value);
  };

  const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'indent',
    'align',
    'lineHeight'
  ];
  const [content, setContent] = useState('');
  const [status, setStatus] = useState('draft');
  const [isSaving, setIsSaving] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [contractId, setContractId] = useState(null);
  const [isContractSigned, setIsContractSigned] = useState(false);
  const sigPad = useRef({});
  const [isSigningError, setIsSigningError] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [repName, setRepName] = useState('');
  const [contractStatus, setContractStatus] = useState({ type: '', message: '' });

  // Split Information States
  const [showSplitInfoModal, setShowSplitInfoModal] = useState(false);
  const [splitInfo, setSplitInfo] = useState({
    client_payout_percent: '',
    company_net_percent: ''
  });
  const [isSavingSplits, setIsSavingSplits] = useState(false);

  // Document Repository States
  const [showDocumentRepo, setShowDocumentRepo] = useState(true); // Start with document repo open
  const [documentTemplates, setDocumentTemplates] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);
  const [partners, setPartners] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveDocumentData, setSaveDocumentData] = useState({
    title: '',
    description: '',
    category: 'general',
    tags: '',
    assignedTo: 'template', // 'template', 'employee', 'client'
    replaceExisting: false
  });
  const [isUpdatingDocument, setIsUpdatingDocument] = useState(false);
  const [existingDocumentId, setExistingDocumentId] = useState(null);

  // Revision/Comment System States
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [revisionData, setRevisionData] = useState({
    title: '',
    reviewerEmail: '',
    reviewerName: '',
    message: '',
    dueDate: ''
  });
  const [isSendingRevision, setIsSendingRevision] = useState(false);
  const [documentComments, setDocumentComments] = useState([]);
  const [showComments, setShowComments] = useState(false);
  const [currentRevisionId, setCurrentRevisionId] = useState(null);
  const [showCommentsView, setShowCommentsView] = useState(false);
  const [hoveredCommentId, setHoveredCommentId] = useState(null);
  const [activeCommentId, setActiveCommentId] = useState(null);

  // AI Document Editing States
  const [showAIEditModal, setShowAIEditModal] = useState(false);
  const [aiSuggestions, setAISuggestions] = useState([]);
  const [isAnalyzingComments, setIsAnalyzingComments] = useState(false);
  const [selectedSuggestions, setSelectedSuggestions] = useState(new Set());
  const [isApplyingEdits, setIsApplyingEdits] = useState(false);
  const [processingCommentId, setProcessingCommentId] = useState(null);

  // AI Template Modification States (for document repository)
  const [showAIModifyModal, setShowAIModifyModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [aiModificationPrompt, setAIModificationPrompt] = useState('');
  const [isModifyingTemplate, setIsModifyingTemplate] = useState(false);

  // Resend Agreement Confirmation State
  const [isResendingConfirmation, setIsResendingConfirmation] = useState(false);
  const [isScanningReplies, setIsScanningReplies] = useState(false);

  // State for suggestion mode
  const [isSuggestionMode, setIsSuggestionMode] = useState(false);
  const [selectedText, setSelectedText] = useState('');
  const [suggestionText, setSuggestionText] = useState('');
  const [showSuggestionDialog, setShowSuggestionDialog] = useState(false);
  const [suggestionType, setSuggestionType] = useState('change'); // 'change', 'remove', 'add'

  useEffect(() => {
    if (isOpen) {
      // Reset ALL states when modal opens or client changes
      setShowDocumentRepo(true);
      setContent('');
      setShowCommentsView(false);
      setDocumentComments([]);
      setCurrentRevisionId(null);
      
      // RESET STATUS - this was missing!
      setStatus('draft');
      setIsContractSigned(false);
      setContractId(null);
      
      // Load document repository
      fetchCategories();
      searchDocumentTemplates();
      
      // Auto-load existing contract if available
      loadExistingContract();
    }
    
    // Cleanup when modal closes
    return () => {
      if (autoSaveTimeout.current) {
        clearTimeout(autoSaveTimeout.current);
      }
    };
  }, [isOpen, client]);

  /**
   * Load existing contract from contracts_collection
   * This loads the latest contract for the client into the editor
   */
  const loadExistingContract = async () => {
    try {
      const clientId = client?.client_id || client?.id;
      if (!clientId) return;

      const response = await axios.get(API_ENDPOINTS.CONTRACTS.CONTRACT(clientId));
      
      if (response.data.status === 'success' && response.data.content) {
        setContent(response.data.content);
        setContractId(response.data.contract_id);
        setStatus(response.data.contract_status || 'draft');
        setIsContractSigned(!!(response.data.client_signature || response.data.rep_signature));
      }
    } catch (error) {
      console.error('Error loading contract:', error);
      // Don't set to empty - keep whatever is there or leave empty
    }
  };

  /**
   * Saves the current document to the NEW document repository
   * This is now the SINGLE SOURCE OF TRUTH for document content
   * @async
   * @returns {Promise<void>}
   */
  const handleSaveDraft = async () => {
    try {
      setIsSaving(true);
      
      // If we have a current revision ID, update that specific revision document
      if (currentRevisionId) {
        const response = await axios.put(API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.UPDATE(currentRevisionId), {
          content: content
        });

        if (response.data.status === 'success') {
          // Document updated
        }
      } else {
        // Save to NEW document repository (single source of truth)
        // This replaces the old clientdb.contracts content storage
        const clientId = client?.client_id || client?.id;
        const clientName = `${client?.First_Legal_Name || ''} ${client?.Last_Legal_Name || ''}`.trim();
        
        const documentData = {
          title: `${clientName} Contract`,
          content: content,
          category: 'client',
          description: `Active contract document for ${clientName}`,
          tags: [`client_${clientId}`, clientName.toLowerCase().replace(/\s+/g, '-'), 'contract', 'active'],
          created_by: 'user',
          assigned_to: 'client'
        };

        console.log('💾 SAVE_DRAFT: Saving to document repository');
        console.log('📋 Document data:', {
          title: documentData.title,
          category: documentData.category,
          assigned_to: documentData.assigned_to,
          content_length: documentData.content.length,
          tags: documentData.tags
        });
        
        // Check if client document already exists
        const searchResponse = await axios.get(
          `${API_ENDPOINTS.CONTRACTS.DOCUMENTS.SEARCH}?query=${encodeURIComponent(documentData.title)}`
        );
        
        console.log('🔍 Search response:', searchResponse.data);
        
        let response;
        if (searchResponse.data.status === 'success' && searchResponse.data.templates.length > 0) {
          const exactMatch = searchResponse.data.templates.find(
            template => template.title === documentData.title && template.assigned_to === 'client'
          );
          
          if (exactMatch) {
            // Get the proper ID (backend returns 'id', not '_id')
            const matchedId = exactMatch.id || exactMatch._id;
            
            console.log('📝 Found existing document:', exactMatch);
            console.log('📝 Document id:', exactMatch.id);
            console.log('📝 Document _id:', exactMatch._id);
            console.log('📝 Using ID:', matchedId);
            
            if (!matchedId || matchedId === 'undefined') {
              console.error('❌ Found document but ID is invalid!', exactMatch);
              console.log('📄 Creating new document instead');
              response = await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.SAVE, documentData);
            } else {
              // Update existing document
              console.log('📝 Updating existing document with ID:', matchedId);
              response = await axios.put(API_ENDPOINTS.CONTRACTS.DOCUMENTS.UPDATE(matchedId), documentData);
              console.log('✅ Update response:', response.data);
            }
          } else {
            // Create new document
            console.log('📄 Creating new document (no exact match found)');
            response = await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.SAVE, documentData);
            console.log('✅ Create response:', response.data);
          }
        } else {
          // Create new document
          console.log('📄 Creating new document (no templates found in search)');
          response = await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.SAVE, documentData);
          console.log('✅ Create response:', response.data);
        }

        if (response.data.status === 'success') {
          const docId = response.data.document_id || response.data.template_id || 'unknown';
          console.log('✅ SAVE_DRAFT: Document saved successfully with ID:', docId);
          
          // Show success status in modal
          setContractStatus({ type: 'success', message: 'Document saved successfully!' });
          
          // Clear status after 3 seconds
          setTimeout(() => {
            setContractStatus({ type: '', message: '' });
          }, 3000);
        } else {
          console.warn('⚠️ SAVE_DRAFT: Response status was not success:', response.data);
          
          // Show warning status
          setContractStatus({ 
            type: 'error', 
            message: 'Save completed but received unexpected response.' 
          });
          
          setTimeout(() => {
            setContractStatus({ type: '', message: '' });
          }, 4000);
        }
      }
    } catch (error) {
      console.error('❌ SAVE_DRAFT ERROR:', error);
      console.error('❌ SAVE_DRAFT ERROR Details:', error.response?.data);
      
      // Show error status in modal
      setContractStatus({ 
        type: 'error', 
        message: 'Failed to save document. Please try again.' 
      });
      
      // Clear error status after 4 seconds
      setTimeout(() => {
        setContractStatus({ type: '', message: '' });
      }, 4000);
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Sends the contract to the client for signature
   * @async
   * @returns {Promise<void>}
   */
  const checkSplitInfo = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.LEADS.GET_SPLIT_INFO(client.client_id));
      
      // splits-company returns data directly, not in a splits array
      if (response.data && response.data.client_payout_percent !== undefined && response.data.company_net_percent !== undefined) {
        setSplitInfo({
          client_payout_percent: response.data.client_payout_percent,
          company_net_percent: response.data.company_net_percent
        });
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error checking split info:', error);
      return false;
    }
  };

  const handleSaveSplitInfo = async () => {
    try {
      setIsSavingSplits(true);

      // Validate input
      const clientPercent = parseFloat(splitInfo.client_payout_percent);
      const companyPercent = parseFloat(splitInfo.company_net_percent);

      if (isNaN(clientPercent) || isNaN(companyPercent)) {
        toast.error('Please enter valid numbers for both percentages');
        return;
      }

      if (clientPercent < 0 || clientPercent > 100 || companyPercent < 0 || companyPercent > 100) {
        toast.error('Percentages must be between 0 and 100');
        return;
      }

      // Save split information to splits-company collection
      const saveResponse = await axios.post(API_ENDPOINTS.LEADS.SAVE_SPLIT_INFO(), {
        clientId: client.client_id,
        client_payout_percent: clientPercent,
        company_net_percent: companyPercent
      });

      if (!saveResponse.data || !saveResponse.data.success) {
        toast.error('Failed to save split information');
        return;
      }

      // VERIFICATION: Fetch back from database to ensure it was saved
      try {
        const verifyResponse = await axios.get(API_ENDPOINTS.LEADS.GET_SPLIT_INFO(client.client_id));
        if (!verifyResponse.data || !verifyResponse.data.client_payout_percent || !verifyResponse.data.company_net_percent) {
          toast.error('❌ Split information was not saved correctly. Please try again.');
          return;
        }
        const savedData = verifyResponse.data;
        if (parseFloat(savedData.client_payout_percent) !== clientPercent || 
            parseFloat(savedData.company_net_percent) !== companyPercent) {
          toast.error('❌ Split information verification failed. Saved values do not match.');
          return;
        }
      } catch (verifyError) {
        console.error('Error verifying split info save:', verifyError);
        toast.error('❌ Could not verify split information was saved. Please check and try again.');
        return;
      }

      // Update state with saved values
      setSplitInfo({
        client_payout_percent: clientPercent,
        company_net_percent: companyPercent
      });

      toast.success('✅ Split information saved successfully!');
      setShowSplitInfoModal(false);
      
      // Proceed with sending contract (will validate again before sending)
      await handleSendContract();
    } catch (error) {
      console.error('Error saving split info:', error);
      toast.error('Failed to save split information: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSavingSplits(false);
    }
  };

  const handleSendToClient = async () => {
    console.log('🚀 SEND_TO_CLIENT: Button clicked!');
    console.log('🚀 SEND_TO_CLIENT: Current state:', { 
      contractId, 
      isSaving, 
      isContractSigned, 
      hasContent: !!content,
      clientId: client._id || client.id 
    });
    
    // ALWAYS show split info modal to confirm splits before sending
    // Load existing split info if it exists
    await checkSplitInfo();
    setShowSplitInfoModal(true);
  };

  const handleSendContract = async () => {
    try {
      setIsSaving(true);
        
        // VALIDATION: Fetch split info from splits-company collection to ensure it's saved
        let splitDataToSend = null;
        try {
          const response = await axios.get(API_ENDPOINTS.LEADS.GET_SPLIT_INFO(client.client_id));
          // splits-company returns data directly, not in a splits array
          if (response.data && response.data.client_payout_percent !== undefined && response.data.company_net_percent !== undefined) {
            splitDataToSend = {
              client_payout_percent: response.data.client_payout_percent,
              company_net_percent: response.data.company_net_percent
            };
          }
        } catch (error) {
          console.error('Error fetching split info:', error);
        }

        // VALIDATION: Require split info to be saved before sending
        if (!splitDataToSend || !splitDataToSend.client_payout_percent || !splitDataToSend.company_net_percent) {
          toast.error('❌ Split information must be saved before sending the contract. Please fill out and save the split information first.');
          setShowSplitInfoModal(true);
          setIsSaving(false);
          return;
        }

        const contractTitle = `${client.First_Legal_Name} ${client.Last_Legal_Name} Contract`;
        
        console.log('Sending contract with data:', {  // Debug log
            contract_id: contractId,
            client_id: client._id || client.id,
            content: content,
            title: contractTitle,
            split_info: splitDataToSend
        });

        const requestData = {
            contract_id: contractId,
            client_id: client._id || client.id,
            content: content,
            title: contractTitle,
            split_info: {
                client_payout_percent: parseFloat(splitDataToSend.client_payout_percent),
                company_net_percent: parseFloat(splitDataToSend.company_net_percent)
            }
        };

        const response = await axios.post(API_ENDPOINTS.CONTRACTS.SEND_TO_CLIENT, requestData);

        console.log('Send response:', response.data);  // Debug log

        if (response.data.status === 'success') {
            const statusMeta = getStatusMeta(response.data.contract_status);
            setStatus(statusMeta.rawStatus);
            setIsContractSigned(statusMeta.isSigned);
            
            console.log('✅ CONTRACT SENT - Setting status message');
            // Show success status in modal
            setContractStatus({ type: 'success', message: 'Contract sent to client successfully!' });
            setShowSplitInfoModal(false);
            
            // Clear status after 3 seconds
            setTimeout(() => {
                setContractStatus({ type: '', message: '' });
            }, 3000);
        }
    } catch (error) {
        console.error('Error sending contract:', error);
        console.error('Error details:', error.response?.data);  // Debug log
        
        // Show error status in modal
        setContractStatus({ 
            type: 'error', 
            message: `Failed to send contract: ${error.response?.data?.detail || error.message}` 
        });
        
        // Clear error status after 4 seconds
        setTimeout(() => {
            setContractStatus({ type: '', message: '' });
        }, 4000);
    } finally {
        setIsSaving(false);
    }
  };

  /**
   * Initiates the representative signing process
   * @async
   * @returns {Promise<void>}
   */
  const handleRepSign = async () => {
    try {
        // First save the contract to get an ID
        const saveResponse = await axios.post(API_ENDPOINTS.CONTRACTS.SAVE_DRAFT, {
            client_id: client._id || client.id,
            content: content
        });

        if (saveResponse.data.status === 'success') {
            // Store the contract ID
            setContractId(saveResponse.data.contract_id);
            // Then show signature pad
            setShowSignaturePad(true);
        }
    } catch (error) {
        console.error('Error preparing for signature:', error);
        alert('Failed to prepare contract for signing');
    }
  };

  /**
   * Handles the signature completion process for both client and representative
   * @async
   * @returns {Promise<void>}
   */
  const handleSign = async () => {
    if (sigPad.current.isEmpty()) {
        setIsSigningError(true);
        return;
    }

    try {
        setIsSigning(true);
        setIsSigningError(false);
        const signatureData = sigPad.current.getTrimmedCanvas().toDataURL('image/png');
        const clientId = client.client_id || "jm" + client.DOB?.replace(/-/g, "");
        
        // If signing as representative
        if (showSignaturePad) {
            const response = await axios.post(API_ENDPOINTS.CONTRACTS.SIGN_REP, {
                contract_id: contractId,
                signature: signatureData,
                name: repName,
                timestamp: new Date().toISOString(),
                ip_address: await fetch('https://api.ipify.org?format=json').then(r => r.json()).then(data => data.ip)
            });

            if (response.data.status === 'success') {
                setShowSignaturePad(false);
                setIsContractSigned(true);
                setStatus('rep_signed');
                
                // Show success status in modal
                setContractStatus({ type: 'success', message: 'Contract signed successfully!' });
                
                // Clear status after 3 seconds
                setTimeout(() => {
                  setContractStatus({ type: '', message: '' });
                }, 3000);
            }
        } else {
            // Client signing logic
            const response = await axios.post(API_ENDPOINTS.CONTRACTS.SIGN, {
                client_id: clientId,
                content: content,
                signature: signatureData,
                name: `${client.First_Legal_Name} ${client.Last_Legal_Name}`,
                timestamp: new Date().toISOString(),
                ip_address: await fetch('https://api.ipify.org?format=json').then(r => r.json()).then(data => data.ip)
            });

            if (response.data.status === 'success') {
                setStatus('signed');
                setIsContractSigned(true);
                setContractId(response.data.contract_id);
                
                // Show success status in modal
                setContractStatus({ type: 'success', message: 'Contract signed successfully!' });
                
                // Clear status after 3 seconds
                setTimeout(() => {
                  setContractStatus({ type: '', message: '' });
                }, 3000);
                
                // Close after a brief delay to show the message
                setTimeout(() => {
                  onClose();
                }, 1500);
            }
        }
    } catch (error) {
        setIsSigningError(true);
        console.error('Error saving signature:', error);
        
        // Show error status in modal
        setContractStatus({ 
          type: 'error', 
          message: `Failed to sign contract: ${error.response?.data?.detail || 'Unknown error'}` 
        });
        
        // Clear error status after 4 seconds
        setTimeout(() => {
          setContractStatus({ type: '', message: '' });
        }, 4000);
    } finally {
        setIsSigning(false);
    }
  };

  /**
   * Downloads the signed contract as a PDF
   * @async
   * @returns {Promise<void>}
   */
  const handleDownload = async () => {
    try {
      if (!contractId) {
        console.error('No contract ID available');
        return;
      }

      const response = await axios.get(
        `${API_ENDPOINTS.CONTRACTS.DOWNLOAD}/${contractId}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'signed_contract.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Show success status in modal
      setContractStatus({ type: 'success', message: 'Contract downloaded successfully!' });
      
      // Clear status after 3 seconds
      setTimeout(() => {
        setContractStatus({ type: '', message: '' });
      }, 3000);
    } catch (error) {
      console.error('Error downloading contract:', error);
      
      // Show error status in modal
      setContractStatus({ 
        type: 'error', 
        message: 'Failed to download contract. Please try again.' 
      });
      
      // Clear error status after 4 seconds
      setTimeout(() => {
        setContractStatus({ type: '', message: '' });
      }, 4000);
    }
  };

  // Document Repository Functions
  const fetchCategories = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.CONTRACTS.DOCUMENTS.CATEGORIES);
      if (response.data.status === 'success') {
        setCategories(response.data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPartners = async () => {
    try {
      console.log('Fetching partners...');
      const response = await axios.get(`${API_ENDPOINTS.API_BASE_URL}/api/partners`);
      console.log('Partners API response:', response.data);
      // The partners API returns an array directly in response.data
      if (Array.isArray(response.data)) {
        console.log('Setting partners:', response.data);
        setPartners(response.data);
      } else {
        console.error('Partners response is not an array:', response.data);
        setPartners([]);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      setPartners([]);
    }
  };

  const updateTemplatePartner = async (templateId, partnerId, partnerName) => {
    try {
      console.log('Updating template partner:', {templateId, partnerId, partnerName});
      const response = await axios.put(API_ENDPOINTS.CONTRACTS.DOCUMENTS.UPDATE(templateId), {
        partner_id: partnerId,
        partner_name: partnerName
      });
      console.log('Update response:', response.data);
      if (response.data.status === 'success') {
        // Refresh templates view - call search with category to show templates
        await searchDocumentTemplates('', selectedCategory);
        toast.success(`Template assigned to ${partnerName}`);
      }
    } catch (error) {
      console.error('Error updating template partner:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Failed to assign partner to template');
    }
  };

  const updateTemplateCategory = async (templateId, category) => {
    try {
      console.log('Updating template category:', {templateId, category});
      const response = await axios.put(API_ENDPOINTS.CONTRACTS.DOCUMENTS.UPDATE(templateId), {
        category: category
      });
      console.log('Update response:', response.data);
      if (response.data.status === 'success') {
        // Refresh templates view
        await searchDocumentTemplates('', selectedCategory);
        const categoryName = category === 'management' ? 'Management Contract' :
                            category === 'all' ? 'All-Inclusive Contract' :
                            category === 'recover' ? 'Recovery Contract' : category;
        toast.success(`Template category set to ${categoryName}`);
      }
    } catch (error) {
      console.error('Error updating template category:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Failed to update template category');
    }
  };

  const searchDocumentTemplates = async (query = '', category = '') => {
    try {
      setIsLoadingDocuments(true);
      const params = new URLSearchParams();
      if (query) params.append('query', query);
      if (category) params.append('category', category);
      
      // Add client_id parameter for backend filtering
      const clientId = client?.client_id || client?.id;
      if (clientId) params.append('client_id', clientId);
      
      const response = await axios.get(
        `${API_ENDPOINTS.CONTRACTS.DOCUMENTS.SEARCH}?${params.toString()}`
      );
      
      if (response.data.status === 'success') {
        const templates = response.data.templates;
        
        // Enrich templates with revision/comment timeline data
        const enrichedTemplates = await Promise.all(templates.map(async (template) => {
          const enrichedTemplate = { ...template };
          
          // If this is a client document, fetch its revisions and contract data
          if (template.assigned_to === 'client' && clientId) {
            try {
              // Fetch revisions for this client
              const revisionsResponse = await axios.get(API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.BY_CLIENT(clientId));
              
              if (revisionsResponse.data.status === 'success' && revisionsResponse.data.revisions) {
                const revisions = revisionsResponse.data.revisions;
                
                // Find the most recent revision
                if (revisions.length > 0) {
                  const latestRevision = revisions[0]; // Already sorted by date on backend
                  enrichedTemplate.latest_revision = {
                    sent_at: latestRevision.created_at,
                    reviewer_name: latestRevision.reviewer_name,
                    status: latestRevision.status,
                    comment_count: latestRevision.comments ? latestRevision.comments.length : 0,
                    last_comment_at: latestRevision.comments && latestRevision.comments.length > 0 
                      ? latestRevision.comments[latestRevision.comments.length - 1].created_at 
                      : null
                  };
                }
              }
            } catch (revisionError) {
              console.error('Error fetching revisions for template:', revisionError);
            }
            
            // Fetch contract timeline data if contract_id exists
            if (template.contract_id) {
              try {
                // The contract data should already be in the template from backend
                // but we can add more detail if needed
                if (template.sent_at) {
                  enrichedTemplate.sent_for_signature_at = template.sent_at;
                }
              } catch (contractError) {
                console.error('Error processing contract data:', contractError);
              }
            }
          }
          
          return enrichedTemplate;
        }));
        
        setDocumentTemplates(enrichedTemplates);
      }
    } catch (error) {
      console.error('Error searching documents:', error);
    } finally {
      setIsLoadingDocuments(false);
    }
  };

  const handleDeleteDocument = async (templateId, templateTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${templateTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await axios.delete(API_ENDPOINTS.CONTRACTS.DOCUMENTS.DELETE(templateId));
      if (response.data.status === 'success') {
        // Refresh document list
        searchDocumentTemplates(searchQuery, selectedCategory);
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      alert('Failed to delete document');
    }
  };

  const handleSaveReusableDocument = async () => {
    try {
      setIsUpdatingDocument(true);
      
      console.log('💾 SAVE REUSABLE DOCUMENT');
      console.log('Existing document ID:', existingDocumentId);
      console.log('Content length:', content?.length);
      console.log('Save data:', saveDocumentData);
      
      // VALIDATION: Check if content exists
      if (!content || content.trim().length === 0) {
        alert('❌ Cannot save empty document. Please add content first.');
        return;
      }
      
      // VALIDATION: Check if updating with invalid ID
      if (existingDocumentId && (existingDocumentId === 'undefined' || existingDocumentId === 'null')) {
        console.error('❌ Invalid document ID for update:', existingDocumentId);
        alert('❌ Invalid document ID. Cannot update. Please try creating a new document instead.');
        setExistingDocumentId(null);
        return;
      }
      
      // Determine tags based on assignment
      let finalTags = saveDocumentData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      
      // Add assignment-based tags
      if (saveDocumentData.assignedTo === 'employee') {
        finalTags.push('employee');
      } else if (saveDocumentData.assignedTo === 'client') {
        finalTags.push('client');
        // Add client-specific tag if available
        if (client?.client_id) {
          finalTags.push(`client_${client.client_id}`);
        }
    } else {
        finalTags.push('template');
      }

      const documentData = {
        title: saveDocumentData.title,
        content: content,
        category: saveDocumentData.category,
        description: saveDocumentData.description,
        tags: finalTags,
        created_by: 'user',
        assigned_to: saveDocumentData.assignedTo
      };

      console.log('📤 Document data to send:', {
        ...documentData,
        content: `${documentData.content.substring(0, 100)}... (${documentData.content.length} chars)`
      });

      let response;
      let isUpdate = false;
      
      if (existingDocumentId) {
        // Update existing document (from edit button)
        console.log('🔄 UPDATING existing document with ID:', existingDocumentId);
        response = await axios.put(API_ENDPOINTS.CONTRACTS.DOCUMENTS.UPDATE(existingDocumentId), documentData);
        isUpdate = true;
        console.log('✅ Update response:', response.data);
      } else if (saveDocumentData.replaceExisting) {
        // Check if document with same title already exists and replace it
        console.log('🔍 Checking if document exists with title:', saveDocumentData.title);
        const searchResponse = await axios.get(
          `${API_ENDPOINTS.CONTRACTS.DOCUMENTS.SEARCH}?query=${encodeURIComponent(saveDocumentData.title)}`
        );
        
        console.log('🔍 Search response:', searchResponse.data);
        
        if (searchResponse.data.status === 'success' && searchResponse.data.templates.length > 0) {
          console.log('🔍 Templates found:', searchResponse.data.templates.length);
          console.log('🔍 First template:', searchResponse.data.templates[0]);
          
          // Find exact title match
          const exactMatch = searchResponse.data.templates.find(
            template => template.title.toLowerCase() === saveDocumentData.title.toLowerCase()
          );
          
          if (exactMatch) {
            // Get the proper ID from the matched template
            const matchedId = exactMatch._id || exactMatch.id;
            
            console.log('🔄 Found existing document:', exactMatch);
            console.log('🔄 Document _id:', exactMatch._id);
            console.log('🔄 Document id:', exactMatch.id);
            console.log('🔄 Using ID:', matchedId);
            
            if (!matchedId || matchedId === 'undefined') {
              console.error('❌ Found document but ID is invalid!', exactMatch);
              alert('❌ Found existing document but ID is invalid. Creating new document instead.');
              response = await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.SAVE, documentData);
            } else {
              // Update existing document
              response = await axios.put(API_ENDPOINTS.CONTRACTS.DOCUMENTS.UPDATE(matchedId), documentData);
              isUpdate = true;
            }
          } else {
            // Create new document
            console.log('📄 No exact match, creating new document');
            response = await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.SAVE, documentData);
          }
        } else {
          // Create new document
          console.log('📄 No documents found, creating new');
          response = await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.SAVE, documentData);
        }
      } else {
        // Always create new document
        console.log('📄 Creating new document');
        response = await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.SAVE, documentData);
      }

      if (response.data.status === 'success') {
        console.log('✅ Document saved successfully!');
        alert(`✅ Document ${isUpdate ? 'updated' : 'created'} successfully!`);
        
        setShowSaveModal(false);
        setSaveDocumentData({
          title: '',
          description: '',
          category: 'general',
          tags: '',
          assignedTo: 'template',
          replaceExisting: false
        });
        setExistingDocumentId(null);
        
        // Refresh document list if repo is open
        if (showDocumentRepo) {
          searchDocumentTemplates(searchQuery, selectedCategory);
        }
      }
    } catch (error) {
      console.error('❌ Error saving document:', error);
      console.error('❌ Error details:', error.response?.data);
      alert(`Failed to save document: ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsUpdatingDocument(false);
    }
  };

  const handleLoadDocument = async (templateId) => {
    try {
      const response = await axios.get(API_ENDPOINTS.CONTRACTS.DOCUMENTS.GET(templateId));
      if (response.data.status === 'success') {
        const template = response.data.template;
        // Replace placeholders with client data
        let processedContent = template.content.replace(/{{CLIENT_NAME}}/g, 
          `${client?.First_Legal_Name || ''} ${client?.Last_Legal_Name || ''}`
        );
        
        setContent(processedContent);
        setShowDocumentRepo(false);
        
        // IMPROVED: Set contract ID if available
        if (template.contract_id) {
          setContractId(template.contract_id);
        } else {
          setContractId(null);
        }
        
        // Get the signing status from the template
        const signingStatus = template.signing_status || template.contract_status || template.status;

        if (signingStatus) {
          const statusMeta = getStatusMeta(signingStatus);
          setStatus(statusMeta.rawStatus);
          setIsContractSigned(statusMeta.isSigned);
          console.log(`✅ Document loaded with status: ${statusMeta.displayLabel} (${statusMeta.rawStatus})`);
        } else if (client?.is_signed) {
          // Fallback to client signing status
          setStatus('fully_executed');
          setIsContractSigned(true);
          console.log('✅ Document loaded - Client is marked as signed');
        } else {
          // Default to draft for new documents
          setStatus('draft');
          setIsContractSigned(false);
          console.log('✅ Document loaded - Default to draft status');
        }
      }
    } catch (error) {
      console.error('Error loading document:', error);
      alert('Failed to load document');
    }
  };

  const handleCreateNewDocument = () => {
    setContent('');
    setShowDocumentRepo(false);
    // Reset status for new document
    setStatus('draft');
    setIsContractSigned(false);
    setContractId(null);
    console.log('✅ New document created - Status reset to draft');
  };

  // Removed handleLoadExistingContract - users now load documents from the repository directly

  const handleEditDocument = async (template) => {
    try {
      console.log('📝 Edit document clicked:', template);
      console.log('Template ID:', template.id, 'Template _id:', template._id);
      
      // Get the proper ID
      const templateId = template.id || template._id;
      
      if (!templateId || templateId === 'undefined') {
        alert('❌ Invalid document ID. Cannot edit this document.');
        console.error('Invalid template ID:', templateId, 'Template:', template);
        return;
      }
      
      console.log('✅ Using template ID:', templateId);
      
      // Load the template content first (so user can see what they're editing)
      const response = await axios.get(API_ENDPOINTS.CONTRACTS.DOCUMENTS.GET(templateId));
      
      if (response.data.status === 'success') {
        const loadedTemplate = response.data.template;
        
        // Load the content into the editor
        setContent(loadedTemplate.content || '');
        console.log('✅ Template content loaded:', loadedTemplate.content?.length, 'characters');
        
        // Populate the save form with template metadata
        setSaveDocumentData({
          title: loadedTemplate.title || template.title,
          description: loadedTemplate.description || template.description || '',
          category: loadedTemplate.category || template.category || 'general',
          tags: ((loadedTemplate.tags || template.tags || []).filter(tag => !['employee', 'client', 'template'].includes(tag))).join(', '),
          assignedTo: loadedTemplate.assigned_to || template.assigned_to || 'template',
          replaceExisting: false
        });
        
        // Store the document ID for updating
        setExistingDocumentId(templateId);
        
        // Show the save modal
        setShowSaveModal(true);
        console.log('✅ Edit modal opened with document ID:', templateId);
      } else {
        alert('Failed to load document for editing');
        console.error('Failed to load document:', response.data);
      }
    } catch (error) {
      console.error('Error loading document for edit:', error);
      alert(`Failed to load document for editing: ${error.message}`);
    }
  };

  const handleSaveAsTemplate = () => {
    setSaveDocumentData({
      title: '',
      description: '',
      category: 'general',
      tags: '',
      assignedTo: 'template',
      replaceExisting: false
    });
    setExistingDocumentId(null);
    setShowSaveModal(true);
  };

  // Removed handleSaveDoc - now using handleSaveDraft which saves directly to document repository

  const handleOpenDocumentRepo = () => {
    setShowDocumentRepo(true);
    fetchCategories();
    searchDocumentTemplates(); // Load all templates initially
  };

  const handleDocumentSearch = () => {
    searchDocumentTemplates(searchQuery, selectedCategory);
  };

  // Revision/Comment System Functions
  const handleSendForRevisions = () => {
    const clientName = `${client?.First_Legal_Name || ''} ${client?.Last_Legal_Name || ''}`.trim();
    setRevisionData({
      title: `${clientName} Contract Review`,
      reviewerEmail: client?.Email_Address || '',
      reviewerName: clientName,
      message: `Please review the attached contract for ${clientName} and provide your feedback.`,
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] // 7 days from now
    });
    setShowRevisionModal(true);
  };

  const handleCreateRevision = async () => {
    if (!revisionData.reviewerEmail || !revisionData.reviewerName || !revisionData.title) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setIsSendingRevision(true);
      
      // Generate client_id using the same logic as the form and loadRecentRevisionComments
      let clientId = client?.client_id;
      
      // If no client_id, generate it from the client data
      if (!clientId && client?.First_Legal_Name && client?.Last_Legal_Name && client?.DOB) {
        const firstInitial = client.First_Legal_Name.charAt(0).toLowerCase();
        const lastInitial = client.Last_Legal_Name.charAt(0).toLowerCase();
        clientId = `${firstInitial}${lastInitial}${client.DOB.replace(/-/g, "")}`;
      }
      
      const response = await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.CREATE, {
        title: revisionData.title,
        content: content,
        reviewer_email: revisionData.reviewerEmail,
        reviewer_name: revisionData.reviewerName,
        message: revisionData.message,
        due_date: revisionData.dueDate,
        client_id: clientId,
        client_name: `${client?.First_Legal_Name || ''} ${client?.Last_Legal_Name || ''}`.trim(),
        created_by: 'user' // You might want to get actual user info
      });

      if (response.data.status === 'success') {
        const revisionId = response.data.revision_id;
        
        // Send email notification
        await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.SEND_EMAIL(revisionId));
        
        setShowRevisionModal(false);
        setRevisionData({
          title: '',
          reviewerEmail: '',
          reviewerName: '',
          message: '',
          dueDate: ''
        });
        
        // Show success status in modal
        setContractStatus({ type: 'success', message: 'Contract sent for revisions successfully!' });
        
        // Clear status after 3 seconds
        setTimeout(() => {
          setContractStatus({ type: '', message: '' });
        }, 3000);
      }
    } catch (error) {
      console.error('Error creating revision:', error);
      
      // Show error status in modal
      setContractStatus({ 
        type: 'error', 
        message: 'Failed to send revision request. Please try again.' 
      });
      
      // Clear error status after 4 seconds
      setTimeout(() => {
        setContractStatus({ type: '', message: '' });
      }, 4000);
    } finally {
      setIsSendingRevision(false);
    }
  };

  const handleLoadComments = async (revisionId) => {
    try {
      const response = await axios.get(API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.COMMENTS.GET(revisionId));
      if (response.data.status === 'success') {
        setDocumentComments(response.data.comments || []);
        setShowComments(true);
      }
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  // Add function to load comments for a specific revision
  const handleLoadCommentsForRevision = async (revisionId) => {
    try {
      const response = await axios.get(API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.GET(revisionId));
      console.log('Revision API response:', response.data);
      
      if (response.data.status === 'success') {
        const revision = response.data.revision;
        const comments = revision.comments || [];
        
        console.log('Revision loaded:', revision);
        console.log('Comments loaded:', comments);
        
        // Load the document content from the revision
        if (revision.content) {
          setContent(revision.content);
        }
        
        // Load the comments
        if (comments.length > 0) {
          setDocumentComments(comments);
          setCurrentRevisionId(revisionId);
          setShowCommentsView(true);
        } else {
          setDocumentComments([]);
          setCurrentRevisionId(revisionId);
          setShowCommentsView(false);
          alert(`Loaded revision "${revision.title}" - no comments found`);
        }
      } else {
        alert('Failed to load revision - API returned error');
      }
    } catch (error) {
      console.error('Error loading revision:', error);
      alert(`Error loading revision: ${error.message}`);
    }
  };

  /**
   * Returns the default contract template with placeholders
   * @returns {string} The default contract template
   */
  const getDefaultTemplate = () => '';

  // Load partners and categories when modal opens
  React.useEffect(() => {
    if (isOpen) {
      fetchPartners();
      fetchCategories();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  // Function to render document content with highlighted comments
  const renderDocumentContentWithComments = () => {
    // Always show the editable Quill editor when not in comments view
    if (!showCommentsView) {
      return (
        <ReactQuill
          ref={quillRef}
          value={content}
          onChange={setContent}
          modules={quillModules}
          formats={quillFormats}
          className="snapped-contract-modal__quill-editor"
          theme="snow"
          style={{ height: '500px', marginBottom: '50px' }}
        />
      );
    }

    // Filter out rejected comments from highlighting
    const inlineComments = documentComments.filter(c => c.is_inline && c.selected_text && !c.rejected);
    
    // Apply highlighting to the full HTML content instead of splitting it
    let processedContent = content;
    
    inlineComments.forEach(comment => {
      const selectedText = comment.selected_text;
      const commentId = comment.id;
      const commentType = comment.comment_type;
      const isActive = activeCommentId === commentId;
      const isHovered = hoveredCommentId === commentId;
      
      const highlightClass = `highlighted-text ${commentType} ${isActive ? 'active' : ''} ${isHovered ? 'hovered' : ''}`;
      
      const highlightedSpan = `<span class="${highlightClass}" 
        data-comment-id="${commentId}" 
        title="${comment.text.substring(0, 100)}...">${selectedText}</span>`;
      
      // Only replace the first occurrence to avoid duplicates
      processedContent = processedContent.replace(selectedText, highlightedSpan);
    });
    
    return (
          <div className="document-content-with-comments">
            <div 
              className="document-content-display ql-editor"
              dangerouslySetInnerHTML={{ __html: processedContent }}
              onClick={handleParagraphClick}
            />
            
            {/* Suggestion Dialog */}
            {showSuggestionDialog && (
              <div className="suggestion-dialog">
                <div className="suggestion-dialog-header">
                  <h3>Make a Suggestion</h3>
                  <button onClick={() => setShowSuggestionDialog(false)}>×</button>
                </div>
                <div className="suggestion-dialog-content">
                  <div className="suggestion-type-selector">
                    <button
                      className={suggestionType === 'change' ? 'active' : ''}
                      onClick={() => setSuggestionType('change')}
                    >
                      ✏️ Change
                    </button>
                    <button
                      className={suggestionType === 'remove' ? 'active' : ''}
                      onClick={() => setSuggestionType('remove')}
                    >
                      🗑️ Remove
                    </button>
                    <button
                      className={suggestionType === 'add' ? 'active' : ''}
                      onClick={() => setSuggestionType('add')}
                    >
                      ➕ Add
                    </button>
                  </div>
                  
                  {selectedText && (
                    <div className="selected-text-display">
                      <strong>Selected Text:</strong>
                      <div className="selected-text">{selectedText}</div>
                    </div>
                  )}
                  
                  {suggestionType !== 'remove' && (
                    <div className="suggestion-input">
                      <strong>{suggestionType === 'change' ? 'Suggested Change:' : 'Text to Add:'}</strong>
                      <textarea
                        value={suggestionText}
                        onChange={(e) => setSuggestionText(e.target.value)}
                        placeholder={suggestionType === 'change' ? 'Enter your suggested text...' : 'Enter text to add...'}
                        rows={4}
                      />
                    </div>
                  )}
                  
                  <div className="suggestion-dialog-actions">
                    <button onClick={() => setShowSuggestionDialog(false)}>Cancel</button>
                    <button 
                      onClick={handleSuggestionSubmit}
                      disabled={!selectedText && suggestionType !== 'add'}
                    >
                      Submit Suggestion
                    </button>
                  </div>
                </div>
              </div>
            )}
        
        <div className="comments-sidebar-modal">
          <div className="comments-header">
            <h4>Comments & Feedback</h4>
            <button 
              onClick={() => setShowCommentsView(false)}
              className="toggle-comments-btn"
            >
              Hide Comments
            </button>
          </div>
          
          {/* Active Comment Display */}
          {activeCommentId && (
            <div className="active-comment-display">
              {(() => {
                const activeComment = documentComments.find(c => c.id === activeCommentId);
                if (!activeComment) return null;
                
                return (
                  <div className={`comment active-comment ${activeComment.comment_type}`}>
                    <div className="comment-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span 
                        className="comment-type-badge"
                        style={{ backgroundColor: getCommentTypeColor(activeComment.comment_type) }}
                      >
                        {getCommentTypeIcon(activeComment.comment_type)} {activeComment.comment_type}
                      </span>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <button
                          onClick={() => processCommentWithAI(activeComment)}
                          disabled={processingCommentId === activeComment.id}
                          data-btn="ai-comment"
                          style={{
                            background: '#e3d5f5',
                            border: '1px solid #7c3aed',
                            fontSize: '14px',
                            cursor: 'pointer',
                            padding: '4px',
                            color: '#7c3aed',
                            lineHeight: '1',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s',
                            borderRadius: '4px',
                            fontWeight: '600',
                            width: '28px',
                            height: '28px'
                          }}
                          onMouseEnter={(e) => {
                            if (processingCommentId !== activeComment.id) {
                              e.target.style.background = '#7c3aed';
                              e.target.style.color = 'white';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#e3d5f5';
                            e.target.style.color = '#7c3aed';
                          }}
                        >
                          <FontAwesomeIcon icon={faMagicWandSparkles} />
                        </button>
                        <button
                          onClick={() => handleResolveComment(activeComment.id)}
                          data-btn="resolve-comment"
                          aria-label="Mark as resolved"
                          style={{
                            background: '#d4edda',
                            border: '1px solid #28a745',
                            fontSize: '14px',
                            cursor: 'pointer',
                            padding: '4px',
                            color: '#28a745',
                            lineHeight: '1',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s',
                            borderRadius: '4px',
                            fontWeight: '600',
                            width: '28px',
                            height: '28px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#28a745';
                            e.target.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#d4edda';
                            e.target.style.color = '#28a745';
                          }}
                        >
                          <FontAwesomeIcon icon={faCheck} />
                        </button>
                        <button
                          onClick={() => handleRejectComment(activeComment.id)}
                          data-btn="reject-comment"
                          aria-label="Reject suggestion"
                          style={{
                            background: '#f8d7da',
                            border: '1px solid #dc3545',
                            fontSize: '14px',
                            cursor: 'pointer',
                            padding: '4px',
                            color: '#dc3545',
                            lineHeight: '1',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s',
                            borderRadius: '4px',
                            fontWeight: '600',
                            width: '28px',
                            height: '28px'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#dc3545';
                            e.target.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#f8d7da';
                            e.target.style.color = '#dc3545';
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </div>
                    </div>
                    <div className="comment-author">
                      <strong>{activeComment.author}</strong>
                      <span className="comment-date">
                        {new Date(activeComment.created_at).toLocaleString()}
                      </span>
                    </div>
                    {activeComment.selected_text && (
                      <div className="selected-text-quote">
                        <strong>Reference:</strong> "{activeComment.selected_text}"
                      </div>
                    )}
                    <div className="comment-text">{activeComment.text}</div>
                  </div>
                );
              })()}
            </div>
          )}
          
          {/* Inline Comments */}
          <div className="inline-comments">
            <h5>Inline Comments ({documentComments.filter(c => c.is_inline).length})</h5>
            {documentComments.filter(c => c.is_inline).map((comment) => (
              <div 
                key={comment.id} 
                className={`comment inline-comment ${comment.resolved ? 'resolved' : ''} ${comment.rejected ? 'rejected' : ''} ${activeCommentId === comment.id ? 'selected' : ''}`}
                data-comment-id={comment.id}
                onClick={() => setActiveCommentId(comment.id)}
                onMouseEnter={() => setHoveredCommentId(comment.id)}
                onMouseLeave={() => setHoveredCommentId(null)}
              >
                <div className="comment-header">
                  <span 
                    className="comment-type-badge"
                    style={{ backgroundColor: getCommentTypeColor(comment.comment_type) }}
                  >
                    {getCommentTypeIcon(comment.comment_type)} {comment.comment_type}
                  </span>
                  <strong>{comment.author}</strong>
                  <span className="comment-date">
                    {new Date(comment.created_at).toLocaleString()}
                  </span>
                </div>
                {comment.selected_text && (
                  <div className="selected-text-quote">
                    <strong>Reference:</strong> "{comment.selected_text.substring(0, 50)}..."
                  </div>
                )}
                <div className="comment-text">{comment.text.substring(0, 100)}...</div>
                {comment.resolved && (
                  <div className="resolved-indicator">✓ Resolved</div>
                )}
                {comment.rejected && (
                  <div className="rejected-indicator">× Rejected</div>
                )}
              </div>
            ))}
          </div>

          {/* General Comments */}
          <div className="general-comments">
            <h5>General Comments</h5>
            <div className="comments-list">
              {documentComments.filter(c => !c.is_inline).length === 0 ? (
                <p className="no-comments">No general comments yet.</p>
              ) : (
                documentComments.filter(c => !c.is_inline).map((comment) => (
                  <div 
                    key={comment.id} 
                    className={`comment ${comment.resolved ? 'resolved' : ''} ${comment.rejected ? 'rejected' : ''} ${activeCommentId === comment.id ? 'selected' : ''}`}
                    onClick={() => setActiveCommentId(comment.id)}
                  >
                    <div className="comment-header">
                      <strong>{comment.author}</strong>
                      <span className="comment-date">
                        {new Date(comment.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="comment-text">{comment.text}</div>
                    {comment.resolved && (
                      <div className="resolved-indicator">✓ Resolved</div>
                    )}
                    {comment.rejected && (
                      <div className="rejected-indicator">× Rejected</div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleParagraphClick = (e) => {
    const target = e.target;
    if (target.classList.contains('highlighted-text')) {
      const commentId = target.getAttribute('data-comment-id');
      setActiveCommentId(commentId);
    }
  };

  const handleTextSelection = () => {
    if (!showCommentsView) return; // Only active when comments view is open
    
    const selection = window.getSelection();
    const selectedText = selection.toString().trim();
    
    if (selectedText && isSuggestionMode) {
      setSelectedText(selectedText);
      setShowSuggestionDialog(true);
    }
  };

  const handleResolveComment = async (commentId) => {
    try {
      if (!currentRevisionId) return;
      
      await axios.put(API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.COMMENTS.RESOLVE(currentRevisionId, commentId));
      
      // Update the UI locally
      setDocumentComments(prev => 
        prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, resolved: true, rejected: false }
            : comment
        )
      );
      
      // Clear active comment if it was the one resolved
      if (activeCommentId === commentId) {
        setActiveCommentId(null);
      }
    } catch (err) {
      console.error('Error resolving comment:', err);
      // Still update UI even if backend fails
      setDocumentComments(prev => 
        prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, resolved: true, rejected: false }
            : comment
        )
      );
      if (activeCommentId === commentId) {
        setActiveCommentId(null);
      }
    }
  };

  const handleRejectComment = async (commentId) => {
    try {
      if (!currentRevisionId) return;
      
      await axios.put(API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.COMMENTS.REJECT(currentRevisionId, commentId));
      
      // Update the UI locally
      setDocumentComments(prev => 
        prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, rejected: true, resolved: false }
            : comment
        )
      );
      
      // Clear active comment if it was the one rejected
      if (activeCommentId === commentId) {
        setActiveCommentId(null);
      }
    } catch (err) {
      console.error('Error rejecting comment:', err);
      // Still update UI even if backend fails
      setDocumentComments(prev => 
        prev.map(comment => 
          comment.id === commentId 
            ? { ...comment, rejected: true, resolved: false }
            : comment
        )
      );
      if (activeCommentId === commentId) {
        setActiveCommentId(null);
      }
    }
  };

  const handleSuggestionSubmit = async () => {
    try {
      if (!selectedText && suggestionType !== 'add') {
        alert('Please select text to suggest changes');
        return;
      }

      const suggestionData = {
        text: suggestionText,
        original_text: selectedText,
        type: 'suggestion',
        suggestion_type: suggestionType,
        is_inline: true,
        comment_type: 'suggestion',
        status: 'pending'
      };

      // Add the suggestion to the current revision
      if (currentRevisionId) {
        const response = await axios.post(
          API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.COMMENTS.CREATE(currentRevisionId),
          suggestionData
        );

        if (response.data.status === 'success') {
          // Add new comment to the list
          setDocumentComments(prev => [response.data.comment, ...prev]);
          
          // Clear suggestion state
          setSelectedText('');
          setSuggestionText('');
          setShowSuggestionDialog(false);
        }
      }
    } catch (error) {
      console.error('Error adding suggestion:', error);
      alert('Failed to add suggestion');
    }
  };

  const getCommentTypeIcon = (type) => {
    switch (type) {
      case 'approval': return '✅';
      case 'rejection': return '❌';
      case 'suggestion': return '💡';
      case 'question': return '❓';
      case 'general': return '💬';
      default: return '💬';
    }
  };

  const getCommentTypeColor = (type) => {
    switch (type) {
      case 'approval': return '#28a745';
      case 'rejection': return '#dc3545';
      case 'suggestion': return '#ffc107';
      case 'question': return '#17a2b8';
      case 'general': return '#6c757d';
      default: return '#6c757d';
    }
  };

  // Function to load comments for recent revisions
  const loadRecentRevisionComments = async () => {
    try {
      // Generate client_id using the same logic as the form
      let clientId = client?.client_id;
      
      // If no client_id, generate it from the client data
      if (!clientId && client?.First_Legal_Name && client?.Last_Legal_Name && client?.DOB) {
        const firstInitial = client.First_Legal_Name.charAt(0).toLowerCase();
        const lastInitial = client.Last_Legal_Name.charAt(0).toLowerCase();
        clientId = `${firstInitial}${lastInitial}${client.DOB.replace(/-/g, "")}`;
      }
      
      if (!clientId) {
        alert('Client ID not available to load revisions.');
        return;
      }

      const response = await axios.get(API_ENDPOINTS.CONTRACTS.DOCUMENTS.REVISIONS.BY_CLIENT(clientId));
      
      if (response.data.status === 'success') {
        if (response.data.revisions && response.data.revisions.length > 0) {
          const latestRevision = response.data.revisions[0];
          
          // Load the document content from the revision
          if (latestRevision.content) {
            setContent(latestRevision.content);
          }
          
          // Extract comments directly from the revision
          const comments = latestRevision.comments || [];
          
          if (comments.length > 0) {
            setDocumentComments(comments);
            setCurrentRevisionId(latestRevision._id);
            setShowCommentsView(true);
          } else {
            setDocumentComments([]);
            setCurrentRevisionId(latestRevision._id);
            setShowCommentsView(false);
            alert(`Loaded revision "${latestRevision.title}" - no comments found`);
          }
        } else {
          alert('No document revisions found for this client. Create revisions using "Send for Revisions" first.');
        }
      } else {
        alert('Error loading revisions: API returned error status');
      }
    } catch (error) {
      console.error('Error loading recent revision comments:', error);
      alert(`Error loading revisions: ${error.message}`);
    }
  };

  // AI Document Editing Functions
  const analyzeCommentsWithAI = async () => {
    if (!documentComments.length) {
      alert('No comments available to analyze. Please load comments first.');
      return;
    }

    try {
      setIsAnalyzingComments(true);
      
      // Prepare data for AI analysis
      const analysisData = {
        document_content: content,
        comments: documentComments.map(comment => ({
          id: comment.id,
          text: comment.text,
          comment_type: comment.comment_type,
          selected_text: comment.selected_text,
          is_inline: comment.is_inline,
          author: comment.author
        })),
        client_name: `${client?.First_Legal_Name || ''} ${client?.Last_Legal_Name || ''}`.trim()
      };

      // Call AI analysis endpoint
      const response = await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.ANALYZE_COMMENTS, analysisData);
      
      if (response.data.status === 'success') {
        setAISuggestions(response.data.suggestions || []);
        setShowAIEditModal(true);
        setSelectedSuggestions(new Set());
      } else {
        alert('Failed to analyze comments with AI');
      }
    } catch (error) {
      console.error('Error analyzing comments with AI:', error);
      alert(`Error analyzing comments: ${error.message}`);
    } finally {
      setIsAnalyzingComments(false);
    }
  };

  const applySelectedEdits = async () => {
    if (selectedSuggestions.size === 0) {
      alert('Please select at least one suggestion to apply.');
      return;
    }

    try {
      setIsApplyingEdits(true);
      
      let updatedContent = content;
      const selectedSuggestionsArray = Array.from(selectedSuggestions);
      
      // Sort suggestions by position (descending) to avoid position shifts during replacement
      const sortedSuggestions = selectedSuggestionsArray
        .map(index => aiSuggestions[index])
        .sort((a, b) => {
          // If we have position info, use it; otherwise process in reverse order
          if (a.position && b.position) {
            return b.position - a.position;
          }
          return selectedSuggestionsArray.indexOf(b) - selectedSuggestionsArray.indexOf(a);
        });

      // Apply each selected suggestion
      for (const suggestion of sortedSuggestions) {
        if (suggestion.original_text && suggestion.suggested_text) {
          // Replace the first occurrence of the original text with the suggested text
          updatedContent = updatedContent.replace(suggestion.original_text, suggestion.suggested_text);
        }
      }

      // Update the content
      setContent(updatedContent);
      setShowAIEditModal(false);
      setSelectedSuggestions(new Set());
      setAISuggestions([]);
      
    } catch (error) {
      console.error('Error applying AI edits:', error);
      alert(`Error applying edits: ${error.message}`);
    } finally {
      setIsApplyingEdits(false);
    }
  };

  const toggleSuggestionSelection = (index) => {
    const newSelected = new Set(selectedSuggestions);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedSuggestions(newSelected);
  };

  // Helper function to find and replace text in HTML content
  const replaceTextInHTML = (htmlContent, originalText, newText) => {
    console.log('🔍 ATTEMPTING TO REPLACE:');
    console.log('Original text to find:', originalText);
    console.log('New text:', newText);
    console.log('HTML content length:', htmlContent.length);
    
    // Helper to normalize quotes and whitespace
    const normalizeText = (text) => {
      return text
        .replace(/[""\u201C\u201D\u0022]/g, '"')  // Normalize all quote types to straight quotes
        .replace(/[''\u2018\u2019\u0027]/g, "'")  // Normalize all apostrophes
        .replace(/\s+/g, ' ')  // Normalize whitespace
        .trim();
    };
    
    // Try exact match first
    if (htmlContent.includes(originalText)) {
      console.log('✅ Found exact match, replacing...');
      return htmlContent.replace(originalText, newText);
    }
    
    // If exact match fails, try HTML-aware replacement
    console.log('⚠️ Exact match failed, trying HTML-aware search...');
    
    // Strip HTML from original text for comparison
    const stripHTML = (html) => {
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    };
    
    // Strip the HTML content to see the plain text
    const htmlStripped = stripHTML(htmlContent);
    console.log('HTML stripped to plain text (first 500 chars):', htmlStripped.substring(0, 500));
    console.log('Full document length:', htmlStripped.length);
    
    // Normalize both texts for comparison
    const normalizedHtmlText = normalizeText(htmlStripped);
    const normalizedOriginalText = normalizeText(originalText);
    
    console.log('Normalized original text (first 100 chars):', normalizedOriginalText.substring(0, 100));
    console.log('Searching in normalized document...');
    
    // Check if plain text contains the original text (exact or normalized)
    let plainTextContainsOriginal = htmlStripped.includes(originalText);
    let useNormalized = false;
    
    if (!plainTextContainsOriginal) {
      console.log('Exact match failed, trying normalized match...');
      plainTextContainsOriginal = normalizedHtmlText.includes(normalizedOriginalText);
      useNormalized = plainTextContainsOriginal;
      console.log('Normalized match?', plainTextContainsOriginal);
    }
    
    console.log('Does full plain text contain the original text?', plainTextContainsOriginal);
    
    if (plainTextContainsOriginal) {
      console.log('✅ Plain text contains the original text! Trying direct HTML search with markers...');
      
      // Try a different approach: search for unique markers in the HTML itself
      // Get first and last few words to use as markers
      const words = originalText.split(/\s+/);
      if (words.length >= 3) {
        const firstWords = words.slice(0, 3).join(' ');
        const lastWords = words.slice(-3).join(' ');
        
        console.log('Searching for first words in HTML:', firstWords);
        console.log('Searching for last words in HTML:', lastWords);
        
        // Try to find the first words in the HTML (they might have quotes/entities)
        const firstWordsVariants = [
          firstWords,
          firstWords.replace(/"/g, '&quot;'),
          firstWords.replace(/"/g, '"'),
          firstWords.replace(/"/g, '"'),
        ];
        
        for (const variant of firstWordsVariants) {
          const startPos = htmlContent.indexOf(variant);
          if (startPos !== -1) {
            console.log('Found start marker at position:', startPos);
            console.log('Marker variant used:', variant);
            
            // Now walk character-by-character from here, collecting plain text until we match originalText
            let htmlIdx = startPos;
            let collectedPlainText = '';
            let endPos = startPos;
            
            while (htmlIdx < htmlContent.length && collectedPlainText.length < originalText.length * 2) {
              if (htmlContent[htmlIdx] === '<') {
                // Skip HTML tag
                const tagEnd = htmlContent.indexOf('>', htmlIdx);
                if (tagEnd !== -1) {
                  htmlIdx = tagEnd + 1;
                  endPos = htmlIdx;
                  continue;
                }
              }
              collectedPlainText += htmlContent[htmlIdx];
              htmlIdx++;
              endPos = htmlIdx;
              
              // Check if we've collected enough and it matches
              const normalizedCollected = normalizeText(collectedPlainText);
              if (normalizedCollected === normalizedOriginalText || collectedPlainText === originalText) {
                console.log('✅ Found perfect match!');
                console.log('Extracted HTML:', htmlContent.substring(startPos, endPos));
                console.log('Extracted text:', collectedPlainText);
                
                // If we're deleting (empty newText), check if this content is inside a list item
                if (newText === '') {
                  console.log('🗑️ Deletion detected, checking if we should delete the entire list item...');
                  
                  // Look backwards from startPos to find if there's an <li> tag
                  const beforeStart = htmlContent.substring(Math.max(0, startPos - 200), startPos);
                  const liStartMatch = beforeStart.lastIndexOf('<li');
                  
                  if (liStartMatch !== -1) {
                    // Found an <li> before our text, now find where it closes
                    const actualLiStart = Math.max(0, startPos - 200) + liStartMatch;
                    const afterEnd = htmlContent.substring(endPos);
                    const liEndMatch = afterEnd.indexOf('</li>');
                    
                    if (liEndMatch !== -1) {
                      const actualLiEnd = endPos + liEndMatch + 5; // +5 for '</li>'
                      
                      console.log('✅ Found surrounding <li> tags!');
                      console.log('List item HTML:', htmlContent.substring(actualLiStart, actualLiEnd));
                      console.log('Deleting entire list item from', actualLiStart, 'to', actualLiEnd);
                      
                      // Delete the entire list item
                      return htmlContent.substring(0, actualLiStart) + htmlContent.substring(actualLiEnd);
                    }
                  }
                  
                  console.log('No surrounding <li> found, deleting just the text');
                }
                
                return htmlContent.substring(0, startPos) + newText + htmlContent.substring(endPos);
              }
            }
            
            console.log('⚠️ Found start marker but couldn match full text');
            console.log('Collected:', collectedPlainText.substring(0, 100));
          }
        }
      }
    }
    
    // Try word-by-word matching
    console.log('⚠️ Character-level match failed, trying word-level match...');
    const originalTextStripped = stripHTML(originalText);
    const words = originalTextStripped.split(/\s+/).filter(w => w.length > 0);
    
    if (words.length > 0) {
      const wordPattern = words
        .map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('\\s*(?:<[^>]*>\\s*)*');
      
      const wordRegex = new RegExp(wordPattern, 'i');
      const wordMatch = htmlContent.match(wordRegex);
      
      if (wordMatch) {
        console.log('✅ Found word-level match:', wordMatch[0]);
        return htmlContent.replace(wordMatch[0], newText);
      }
    }
    
    // Last resort: try to find by a portion of the text
    console.log('⚠️ Word-level match failed, trying partial match...');
    
    if (words.length >= 5) {
      // Try first 5 words
      const partialPattern = words.slice(0, 5)
        .map(word => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
        .join('\\s*(?:<[^>]*>\\s*)*');
      
      const partialRegex = new RegExp(partialPattern + '.*?' + words[words.length - 1].replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
      const partialMatch = htmlContent.match(partialRegex);
      
      if (partialMatch) {
        console.log('✅ Found partial match:', partialMatch[0]);
        // For safety, show what we're replacing
        if (window.confirm(`Found match:\n\n"${partialMatch[0].substring(0, 200)}..."\n\nReplace with:\n\n"${newText}"?`)) {
          return htmlContent.replace(partialMatch[0], newText);
        }
      }
    }
    
    console.error('❌ Could not find text in any way');
    return null; // No match found
  };

  // New function to process individual comment with AI
  const processCommentWithAI = async (comment) => {
    if (!comment || !comment.text) {
      alert('Invalid comment data');
      return;
    }

    try {
      setProcessingCommentId(comment.id);
      
      // Get the CURRENT content from the Quill editor (not stale state)
      const currentContent = quillRef.current?.getEditor()?.root?.innerHTML || content;
      
      console.log('📄 Getting content from editor...');
      console.log('Content state length:', content.length);
      console.log('Editor content length:', currentContent.length);
      console.log('Using editor content:', currentContent !== content);
      
      // Prepare data for AI analysis of single comment
      const analysisData = {
        document_content: currentContent,
        comments: [comment], // Only send this specific comment
        client_name: `${client?.First_Legal_Name || ''} ${client?.Last_Legal_Name || ''}`.trim()
      };

      console.log('🤖 Sending to AI:', {
        contentLength: currentContent.length,
        commentText: comment.text,
        selectedText: comment.selected_text
      });

      // Call AI analysis endpoint
      console.log('📡 Calling AI endpoint...');
      const response = await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.ANALYZE_COMMENTS, analysisData);
      
      console.log('📥 Got response!');
      console.log('Response status code:', response.status);
      console.log('Response data type:', typeof response.data);
      
      try {
        console.log('🤖 AI Response:', JSON.stringify(response.data, null, 2));
      } catch (e) {
        console.error('Failed to stringify response:', e);
        console.log('Raw response.data:', response.data);
      }
      
      console.log('Status:', response.data?.status);
      console.log('Suggestions count:', response.data?.suggestions?.length);
      
      if (response.data.status === 'success' && response.data.suggestions.length > 0) {
        const suggestions = response.data.suggestions;
        
        // Apply the first (best) suggestion automatically
        const bestSuggestion = suggestions[0];
        
        console.log('💡 Best suggestion:', bestSuggestion);
        console.log('Has original_text?', !!bestSuggestion.original_text);
        console.log('Has suggested_text?', 'suggested_text' in bestSuggestion);
        
        // Check if we have original_text and suggested_text exists (even if empty string for deletion)
        if (bestSuggestion.original_text && ('suggested_text' in bestSuggestion)) {
          console.log('🔄 Attempting to replace text...');
          
          // Use smart HTML-aware replacement on the CURRENT editor content
          let updatedContent;
          try {
            updatedContent = replaceTextInHTML(
              currentContent, 
              bestSuggestion.original_text, 
              bestSuggestion.suggested_text
            );
            console.log('📝 Replace function returned:', updatedContent ? 'SUCCESS' : 'NULL');
          } catch (replaceError) {
            console.error('❌ Error in replaceTextInHTML:', replaceError);
            alert(`Error trying to replace text: ${replaceError.message}`);
            return;
          }
          
          if (updatedContent) {
            setContent(updatedContent);
            
            // Show success message with details
            const isDeletion = bestSuggestion.suggested_text === '';
            if (isDeletion) {
              alert(`✅ Text Deleted!\n\nRemoved: "${bestSuggestion.original_text.substring(0, 150)}${bestSuggestion.original_text.length > 150 ? '...' : ''}"\n\nReason: ${bestSuggestion.reasoning || 'Deleted as requested'}`);
            } else {
              alert(`✅ AI Edit Applied!\n\nOriginal: "${bestSuggestion.original_text.substring(0, 100)}${bestSuggestion.original_text.length > 100 ? '...' : ''}"\n\nChanged to: "${bestSuggestion.suggested_text.substring(0, 100)}${bestSuggestion.suggested_text.length > 100 ? '...' : ''}"\n\nReason: ${bestSuggestion.reasoning || 'Applied suggestion'}`);
            }
          } else {
            // Couldn't find the text - offer manual edit
            alert(`❌ Could not find exact text to replace.\n\nThe AI suggested changing:\n"${bestSuggestion.original_text}"\n\nTo:\n"${bestSuggestion.suggested_text}"\n\nPlease make this change manually in the editor.\n\nReason: ${bestSuggestion.reasoning || 'Suggested edit'}`);
          }
        } else {
          // AI didn't provide structured data - try to use the comment's selected_text
          console.warn('⚠️ AI returned description only, no structured edit data');
          
          if (comment.selected_text) {
            // If comment has selected text, offer to delete/modify it
            const suggestion = bestSuggestion.description || bestSuggestion.reasoning || 'No description provided';
            const action = suggestion.toLowerCase().includes('remove') || suggestion.toLowerCase().includes('delete') 
              ? 'DELETE' 
              : 'MODIFY';
            
            if (action === 'DELETE') {
              const confirmDelete = window.confirm(
                `🤖 AI Suggestion: ${suggestion}\n\n` +
                `The highlighted text is:\n"${comment.selected_text}"\n\n` +
                `Would you like to DELETE this text?`
              );
              
              if (confirmDelete) {
                const updatedContent = replaceTextInHTML(currentContent, comment.selected_text, '');
                if (updatedContent) {
                  setContent(updatedContent);
                } else {
                  alert('❌ Could not find the text to delete. Please delete it manually:\n\n"' + comment.selected_text + '"');
                }
              }
            } else {
              alert(
                `🤖 AI Suggestion: ${suggestion}\n\n` +
                `The highlighted text is:\n"${comment.selected_text}"\n\n` +
                `Please make the suggested changes manually in the editor.`
              );
            }
          } else {
            // No selected text either - just show the suggestion
            const suggestion = bestSuggestion.description || bestSuggestion.reasoning || 'No description provided';
            alert(
              `🤖 AI Suggestion:\n${suggestion}\n\n` +
              `⚠️ The AI couldn't identify specific text to change automatically.\n\n` +
              `Please apply this suggestion manually in the editor.\n\n` +
              `💡 Tip: Try selecting specific text when making the comment for better AI suggestions.`
            );
          }
        }
      } else {
        alert('AI couldn\'t generate a specific edit for this comment. The comment might be too general or already addressed.');
      }
    } catch (error) {
      console.error('❌ Error processing comment with AI:', error);
      console.error('Error details:', error.response?.data);
      alert(`Error processing comment: ${error.response?.data?.detail || error.message}`);
    } finally {
      setProcessingCommentId(null);
    }
  };

  // AI Template Modification Function (for document repository)
  const handleAIModifyTemplate = async (template) => {
    setSelectedTemplate(template);
    setAIModificationPrompt('');
    setShowAIModifyModal(true);
  };

  const handleResendAgreementConfirmation = async () => {
    try {
      setIsResendingConfirmation(true);
      
      const clientId = client?.client_id || client?.id;
      const clientName = `${client?.First_Legal_Name || ''} ${client?.Last_Legal_Name || ''}`.trim();
      const clientEmail = client?.Email_Address || '';
      const formSource = client?.source || 'leads_company_test926'; // Use default for testing if no source
      
      if (!clientId || !clientName || !clientEmail) {
        toast.error('Missing required client information');
        return;
      }
      
      console.log('📧 Resending agreement confirmation:', {
        clientId,
        clientName,
        formSource,
        hasSource: !!client?.source
      });
      
      const response = await axios.post(`${API_ENDPOINTS.API_BASE_URL}/api/contracts/agreements/create`, {
        client_id: clientId,
        client_name: clientName,
        client_email: clientEmail,
        form_source: formSource
      });
      
      if (response.data.status === 'success') {
        toast.success('✅ Agreement confirmation email sent successfully!');
        setContractStatus({ type: 'success', message: 'Agreement confirmation sent!' });
        setTimeout(() => {
          setContractStatus({ type: '', message: '' });
        }, 3000);
      } else {
        toast.error('Failed to send agreement confirmation');
      }
    } catch (error) {
      console.error('Error resending agreement confirmation:', error);
      toast.error(`Failed to send: ${error.response?.data?.detail || error.message}`);
      setContractStatus({ 
        type: 'error', 
        message: 'Failed to send agreement confirmation' 
      });
      setTimeout(() => {
        setContractStatus({ type: '', message: '' });
      }, 4000);
    } finally {
      setIsResendingConfirmation(false);
    }
  };

  const handleScanForReplies = async () => {
    try {
      setIsScanningReplies(true);
      
      console.log('🔍 Manually triggering reply scanner...');
      
      const response = await axios.post(`${API_ENDPOINTS.API_BASE_URL}/api/contracts/agreements/scan-replies`);
      
      if (response.data.status === 'success') {
        const processed = response.data.processed_count || 0;
        toast.success(`✅ Scan complete! Processed ${processed} reply(ies)`);
        setContractStatus({ type: 'success', message: `Scanned for replies - ${processed} processed` });
        setTimeout(() => {
          setContractStatus({ type: '', message: '' });
        }, 3000);
      } else {
        toast.error('Failed to scan for replies');
      }
    } catch (error) {
      console.error('Error scanning for replies:', error);
      toast.error(`Failed to scan: ${error.response?.data?.detail || error.message}`);
      setContractStatus({ 
        type: 'error', 
        message: 'Failed to scan for replies' 
      });
      setTimeout(() => {
        setContractStatus({ type: '', message: '' });
      }, 4000);
    } finally {
      setIsScanningReplies(false);
    }
  };

  const handleApplyAIModification = async () => {
    if (!aiModificationPrompt.trim() || !selectedTemplate) {
      alert('Please enter modification instructions.');
      return;
    }

    try {
      setIsModifyingTemplate(true);
      
      const clientName = `${client?.First_Legal_Name || ''} ${client?.Last_Legal_Name || ''}`.trim();
      
      // GUARDRAIL 1: Always load the template content first
      console.log('🔒 GUARDRAIL: Loading template content before AI modification');
      
      // Get template ID with proper validation
      const templateId = selectedTemplate.id || selectedTemplate._id;
      if (!templateId || templateId === 'undefined' || templateId === 'null') {
        alert('❌ GUARDRAIL VIOLATION: Template ID is missing or invalid. Cannot proceed with AI modification.');
        return;
      }
      
      const templateResponse = await axios.get(API_ENDPOINTS.CONTRACTS.DOCUMENTS.GET(templateId));
      
      if (templateResponse.data.status !== 'success') {
        alert('❌ GUARDRAIL VIOLATION: Failed to load template content. Cannot proceed without existing template.');
        return;
      }
      
      const templateContent = templateResponse.data.template.content;
      
      // GUARDRAIL 2: Verify template content exists and is not empty
      if (!templateContent || templateContent.trim().length === 0) {
        alert('❌ GUARDRAIL VIOLATION: Template content is empty. Cannot modify empty template.');
        return;
      }
      
      console.log('✅ GUARDRAIL: Template content loaded successfully');
      console.log(`📄 Template: "${selectedTemplate.title}" (${templateContent.length} characters)`);
      console.log(`🔧 Modification request: "${aiModificationPrompt}"`);
      
      // GUARDRAIL 3: Ensure we're using template modification, not document creation
      const requestData = {
        document_content: templateContent,  // MUST provide existing template
        prompt: `MODIFY THE EXISTING TEMPLATE with these changes: ${aiModificationPrompt}`,
        client_name: clientName,
        action: 'create_document'  // This will be handled as template modification due to backend guardrails
      };
      
      // GUARDRAIL 4: Verify request data before sending
      if (!requestData.document_content || requestData.document_content.trim().length === 0) {
        alert('❌ GUARDRAIL VIOLATION: No template content to modify. Aborting AI request.');
        return;
      }
      
      console.log('🔒 GUARDRAIL: Sending template modification request to AI');
      console.log(`📊 Request data: Template length: ${requestData.document_content.length}, Prompt: "${requestData.prompt}"`);
      
      // Step 2: Use AI to modify the template (with backend guardrails)
      const response = await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.ANALYZE_COMMENTS, requestData);
      
      if (response.data.status === 'success' && response.data.generated_document) {
        const modifiedContent = response.data.generated_document;
        
        // GUARDRAIL 5: Verify the AI actually modified the template (not created new content)
        const originalLength = templateContent.length;
        const modifiedLength = modifiedContent.length;
        
        console.log('✅ GUARDRAIL: AI modification completed');
        console.log(`📊 Length comparison: Original: ${originalLength}, Modified: ${modifiedLength}`);
        
        // Additional verification that content was actually modified, not replaced
        const similarityCheck = modifiedContent.includes(templateContent.substring(0, Math.min(100, templateContent.length)));
        if (!similarityCheck && modifiedLength < originalLength * 0.3) {
          console.warn('⚠️ GUARDRAIL WARNING: Modified content appears significantly different from original template');
          const confirmProceed = window.confirm(
            `⚠️ GUARDRAIL WARNING: The AI-modified content appears significantly different from your original template.\n\n` +
            `Original template: ${originalLength} characters\n` +
            `Modified content: ${modifiedLength} characters\n\n` +
            `This might indicate the AI created new content instead of modifying your template.\n\n` +
            `Do you want to proceed anyway?`
          );
          
          if (!confirmProceed) {
            console.log('🔒 GUARDRAIL: User cancelled due to content similarity warning');
            return;
          }
        }
        
        // Load the modified content into the editor
        setContent(modifiedContent);
        
        // AUTO-SAVE: Save the AI-generated document to the repository immediately
        try {
          const clientId = client?.client_id || client?.id;
          const documentData = {
            title: `${clientName} Contract`,
            content: modifiedContent,
            category: 'client',
            description: `AI-generated contract document for ${clientName}`,
            tags: [`client_${clientId}`, clientName.toLowerCase().replace(/\s+/g, '-'), 'contract', 'active', 'ai-generated'],
            created_by: 'user',
            assigned_to: 'client'
          };

          console.log('💾 AUTO-SAVING AI-generated document to repository...');
          
          // Check if client document already exists
          const searchResponse = await axios.get(
            `${API_ENDPOINTS.CONTRACTS.DOCUMENTS.SEARCH}?query=${encodeURIComponent(documentData.title)}`
          );
          
          let saveResponse;
          if (searchResponse.data.status === 'success' && searchResponse.data.templates.length > 0) {
            const exactMatch = searchResponse.data.templates.find(
              template => template.title === documentData.title && template.assigned_to === 'client'
            );
            
            if (exactMatch) {
              // Get the proper ID (backend returns 'id', not '_id')
              const matchedId = exactMatch.id || exactMatch._id;
              
              if (!matchedId || matchedId === 'undefined') {
                console.error('❌ AUTO-SAVE: Found document but ID is invalid!', exactMatch);
                console.log('📄 AUTO-SAVE: Creating new document instead');
                saveResponse = await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.SAVE, documentData);
              } else {
                // Update existing document
                console.log('📝 AUTO-SAVE: Updating existing document with ID:', matchedId);
                saveResponse = await axios.put(API_ENDPOINTS.CONTRACTS.DOCUMENTS.UPDATE(matchedId), documentData);
                console.log('✅ AUTO-SAVE: Updated existing document in repository');
              }
            } else {
              // Create new document
              saveResponse = await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.SAVE, documentData);
              console.log('✅ AUTO-SAVE: Created new document in repository');
            }
          } else {
            // Create new document
            saveResponse = await axios.post(API_ENDPOINTS.CONTRACTS.DOCUMENTS.SAVE, documentData);
            console.log('✅ AUTO-SAVE: Created new document in repository');
          }

          if (saveResponse.data.status === 'success') {
            console.log('✅ AUTO-SAVE: Document saved successfully');
          }
        } catch (saveError) {
          console.error('❌ AUTO-SAVE ERROR:', saveError);
          // Don't fail the whole operation if auto-save fails
          alert('⚠️ Document created but auto-save failed. Please click "Save Document" manually.');
        }
        
        // Close modals
        setShowAIModifyModal(false);
        setShowDocumentRepo(false);
        setAIModificationPrompt('');
        setSelectedTemplate(null);
        
        console.log('✅ GUARDRAIL: Template modification completed successfully');
      } else {
        console.error('❌ GUARDRAIL: AI modification failed - no content returned');
        alert('❌ GUARDRAIL VIOLATION: Failed to modify template. The AI did not return modified content.');
      }
    } catch (error) {
      console.error('❌ GUARDRAIL ERROR:', error);
      if (error.response?.data?.detail?.includes('GUARDRAIL VIOLATION')) {
        alert(`❌ GUARDRAIL VIOLATION: ${error.response.data.detail}`);
      } else {
        alert(`❌ Error modifying template: ${error.message}`);
      }
    } finally {
      setIsModifyingTemplate(false);
    }
  };

  return (
    <div className="snapped-contract-modal-overlay" onClick={onClose}>
      <div className="snapped-contract-modal" onClick={e => e.stopPropagation()}>
        {!showSignaturePad ? (
          <>
            <div className="snapped-contract-modal__header">
              <h2 className="snapped-contract-modal__title">
                {client?.First_Legal_Name} {client?.Last_Legal_Name} Documents
              </h2>
              <div className="snapped-contract-modal__header-controls">
              {!showDocumentRepo && (
                <div className="snapped-contract-modal__status">
                  {getStatusMeta(status).displayLabel.toUpperCase()}
                </div>
              )}
                <button 
                  onClick={onClose}
                  className="snapped-contract-modal__close-btn"
                  title="Close modal"
                >
                  ×
                </button>
              </div>
            </div>

            {!showDocumentRepo && (
              <>
                <div className="snapped-contract-modal__editor">
                  {renderDocumentContentWithComments()}
                </div>

                {/* Contract Status Message */}
                {contractStatus.message && (
                  <div className={`contract-status-message contract-status-${contractStatus.type}`}>
                    {contractStatus.message}
                  </div>
                )}

                <div className="snapped-contract-modal__actions">
                  <button 
                    onClick={handleSaveDraft} 
                    className="snapped-contract-modal__btn snapped-contract-modal__btn--save"
                    disabled={isSaving || isContractSigned || status === 'rep_signed'}
                  >
                    {isSaving ? 'Saving...' : 'Save Document'}
                  </button>
                  <button 
                    onClick={handleSaveAsTemplate}
                    className="snapped-contract-modal__btn snapped-contract-modal__btn--document"
                    disabled={!content}
                  >
                    Save as Template
                  </button>
                  <button 
                    onClick={handleOpenDocumentRepo}
                    className="snapped-contract-modal__btn snapped-contract-modal__btn--document"
                  >
                    Back to Documents
                  </button>
                  <button 
                    onClick={handleResendAgreementConfirmation}
                    className="snapped-contract-modal__btn snapped-contract-modal__btn--revision"
                    disabled={isResendingConfirmation}
                    title="Resend agreement confirmation email to sales team"
                  >
                    {isResendingConfirmation ? 'Sending...' : '📧 Resend Confirmation'}
                  </button>
                  <button 
                    onClick={handleScanForReplies}
                    className="snapped-contract-modal__btn snapped-contract-modal__btn--revision"
                    disabled={isScanningReplies}
                    title="Check for email replies and process agreements now"
                  >
                    {isScanningReplies ? 'Scanning...' : '🔍 Check for Replies'}
                  </button>
                  <button 
                    onClick={handleSendForRevisions}
                    className="snapped-contract-modal__btn snapped-contract-modal__btn--revision"
                    disabled={!content}
                  >
                    Send for Revisions
                  </button>
                  <button 
                    onClick={loadRecentRevisionComments}
                    className="snapped-contract-modal__btn snapped-contract-modal__btn--comments"
                    disabled={!content}
                  >
                    Load Comments
                  </button>
                  {showCommentsView && (
                    <button 
                      onClick={() => setShowCommentsView(false)}
                      className="snapped-contract-modal__btn snapped-contract-modal__btn--comments-toggle"
                    >
                      Hide Comments
                    </button>
                  )}
                  <button 
                    onClick={handleRepSign} 
                    className="snapped-contract-modal__btn snapped-contract-modal__btn--sign"
                    disabled={isContractSigned || status === 'rep_signed'}
                  >
                    Sign as Representative
                  </button>
                  <button 
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSendToClient();
                    }}
                    className="snapped-contract-modal__btn snapped-contract-modal__btn--send"
                    disabled={isSaving || status === 'fully_executed'}
                  >
                    Send to Client for Signature
                  </button>
                </div>
              </>
            )}
          </>
        ) : (
          <div className="signature-container">
            <h3>Please sign below</h3>
            {showSignaturePad && (
              <div className="name-input-container">
                <label htmlFor="repName">Representative Name:</label>
                <input
                  type="text"
                  id="repName"
                  placeholder="Enter your full name"
                  onChange={(e) => setRepName(e.target.value)}
                  className="representative-name-input"
                />
              </div>
            )}
            <SignaturePad
              ref={sigPad}
              canvasProps={{
                className: "signature-pad"
              }}
            />
            <div className="signature-actions">
              <button onClick={() => sigPad.current.clear()}>Clear</button>
              <button 
                onClick={handleSign}
                disabled={isSigning || (showSignaturePad && !repName)}
              >
                {isSigning ? 'Signing...' : 'Complete Signing'}
              </button>
              {status === 'signed' && (
                <button onClick={handleDownload}>
                  Download Signed Contract
                </button>
              )}
            </div>
            {isSigningError && (
              <div className="error-message">
                Failed to save signature. Please try again.
              </div>
            )}
          </div>
        )}
        
        {/* Document Repository Modal */}
        {showDocumentRepo && (
          <div className="document-repo-modal">
            <div className="document-repo-modal__header">
              <h3>Document Repository - {client?.First_Legal_Name} {client?.Last_Legal_Name}</h3>
              <button 
                onClick={() => setShowDocumentRepo(false)}
                className="document-repo-modal__close"
              >
                ×
              </button>
            </div>
            
            <div className="document-repo-modal__new-doc-action">
              <button 
                onClick={handleSaveAsTemplate}
                className="document-repo-modal__new-template-btn"
              >
                + Create New Template
              </button>
            </div>
            
            <div className="document-repo-modal__search">
              <input
                type="text"
                placeholder="Search documents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="document-repo-modal__search-input"
              />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="document-repo-modal__category-select"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat._id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
              <button 
                onClick={handleDocumentSearch}
                className="document-repo-modal__search-btn"
              >
                Search
              </button>
            </div>
            
            <div className="document-repo-modal__filter-info">
              <small>
                📋 Showing templates and documents for {client?.First_Legal_Name} {client?.Last_Legal_Name}
              </small>
            </div>

            <div className="document-repo-modal__results">
              {isLoadingDocuments ? (
                <div className="loading">Loading documents...</div>
              ) : (
                <div className="document-list">
                  {documentTemplates.map(template => (
                    <div key={template._id || template.id} className="document-item">
                      <div className="document-item__header">
                        <h4>{template.title}</h4>
                        <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
                          {template.category && template.category !== 'general' && (
                            <span 
                              style={{
                                padding: '3px 8px',
                                fontSize: '9px',
                                fontWeight: '700',
                                letterSpacing: '0.5px',
                                borderRadius: '3px',
                                textTransform: 'uppercase',
                                backgroundColor: 
                                  template.category === 'management' ? '#dbeafe' :
                                  template.category === 'all' ? '#d1fae5' :
                                  template.category === 'recover' ? '#fed7aa' :
                                  '#e5e7eb',
                                color: 
                                  template.category === 'management' ? '#1e40af' :
                                  template.category === 'all' ? '#065f46' :
                                  template.category === 'recover' ? '#92400e' :
                                  '#374151'
                              }}
                            >
                              {template.category === 'management' ? 'MGMT' :
                               template.category === 'all' ? 'ALL' :
                               template.category === 'recover' ? 'RCVR' :
                               template.category.substring(0, 4).toUpperCase()}
                            </span>
                          )}
                          {template.assigned_to === 'client' && (() => {
                            const statusMeta = getStatusMeta(template.signing_status || template.contract_status || template.status);
                            return (
                              <span
                                className="document-item__category"
                                style={{ background: statusMeta.badgeColor, color: 'white' }}
                              >
                                {statusMeta.badgeLabel}
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      <p className="document-item__description">{template.description}</p>
                      
                      {/* Partner Assignment Dropdown - Only for templates */}
                      {template.assigned_to === 'template' && (
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px'}}>
                          <div className="document-item__partner">
                            <label style={{fontSize: '10px', color: '#6b7280', display: 'block', marginBottom: '3px', fontWeight: '600'}}>
                              PARTNER
                            </label>
                            <select
                              value={template.partner_id || ''}
                              onChange={(e) => {
                                const selectedPartner = partners.find(p => p.id === e.target.value);
                                updateTemplatePartner(
                                  template.id || template._id,
                                  e.target.value,
                                  selectedPartner?.name || ''
                                );
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                fontSize: '12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                fontWeight: '500'
                              }}
                            >
                              <option value="">-- No Partner --</option>
                              {partners.map(partner => (
                                <option key={partner.id} value={partner.id}>
                                  {partner.name}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div className="document-item__category">
                            <label style={{fontSize: '10px', color: '#6b7280', display: 'block', marginBottom: '3px', fontWeight: '600'}}>
                              TYPE
                            </label>
                            <select
                              value={template.category || 'general'}
                              onChange={(e) => {
                                updateTemplateCategory(
                                  template.id || template._id,
                                  e.target.value
                                );
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                fontSize: '12px',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                fontWeight: '500'
                              }}
                            >
                              <option value="general">General</option>
                              <option value="management">Management</option>
                              <option value="all">All-Inclusive</option>
                              <option value="recover">Recovery</option>
                            </select>
                          </div>
                        </div>
                      )}
                      
                      <div className="document-item__meta">
                        <span>Used {template.usage_count} times</span>
                        <span>Created: {new Date(template.created_at).toLocaleDateString()}</span>
                        <span className="document-item__assignment">
                          {template.assigned_to === 'employee' ? '👤 Employee' : 
                           template.assigned_to === 'client' ? '👥 Client' : 
                           '📋 Template'}
                        </span>
                      </div>
                      
                      {/* Timeline Information */}
                      {template.assigned_to === 'client' && (
                        <div className="document-item__timeline">
                          {template.latest_revision && (
                            <>
                              <div className="timeline-event">
                                <span className="timeline-icon">📝</span>
                                <span className="timeline-text">
                                  Sent for review: {new Date(template.latest_revision.sent_at).toLocaleDateString()}
                                  {template.latest_revision.reviewer_name && ` to ${template.latest_revision.reviewer_name}`}
                                </span>
                              </div>
                              {template.latest_revision.status === 'approved' && template.reviewed_at && (
                                <div className="timeline-event" style={{ color: '#10b981', fontWeight: '600' }}>
                                  <span className="timeline-icon">✅</span>
                                  <span className="timeline-text">
                                    Approved: {new Date(template.reviewed_at).toLocaleDateString()}
                                    {template.reviewed_by && ` by ${template.reviewed_by}`}
                                  </span>
                                </div>
                              )}
                              {template.latest_revision.comment_count > 0 && (
                                <div className="timeline-event">
                                  <span className="timeline-icon">💬</span>
                                  <span className="timeline-text">
                                    {template.latest_revision.comment_count} comment{template.latest_revision.comment_count !== 1 ? 's' : ''}
                                    {template.latest_revision.last_comment_at && 
                                      ` (latest: ${new Date(template.latest_revision.last_comment_at).toLocaleDateString()})`
                                    }
                                  </span>
                                </div>
                              )}
                            </>
                          )}
                          {template.revision_status === 'approved' && template.reviewed_at && !template.latest_revision && (
                            <div className="timeline-event" style={{ color: '#10b981', fontWeight: '600' }}>
                              <span className="timeline-icon">✅</span>
                              <span className="timeline-text">
                                Contract Approved: {new Date(template.reviewed_at).toLocaleDateString()}
                                {template.reviewed_by && ` by ${template.reviewed_by}`}
                              </span>
                            </div>
                          )}
                          {template.sent_for_signature_at && (
                            <div className="timeline-event">
                              <span className="timeline-icon">✍️</span>
                              <span className="timeline-text">
                                Sent for signature: {new Date(template.sent_for_signature_at).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="document-item__tags">
                        {template.tags && template.tags.map(tag => (
                          <span key={tag} className={`document-tag ${
                            tag === 'employee' ? 'document-tag--employee' :
                            tag === 'client' ? 'document-tag--client' :
                            tag === 'template' ? 'document-tag--template' :
                            tag.startsWith('client_') ? 'document-tag--client-specific' :
                            'document-tag--custom'
                          }`}>
                            {tag}
                          </span>
                        ))}
                      </div>
                      <div className="document-item__actions">
                        <button 
                          onClick={() => handleLoadDocument(template.id || template._id)}
                          className="document-item__load-btn"
                        >
                          Load Document
                        </button>
                        <button 
                          onClick={() => handleEditDocument(template)}
                          className="document-item__edit-btn"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleDeleteDocument(template.id || template._id, template.title)}
                          className="document-item__delete-btn"
                        >
                          Delete
                        </button>
                        {template.assigned_to === 'template' && (
                          <button 
                            onClick={() => handleAIModifyTemplate(template)}
                            className="document-item__ai-modify-btn"
                            disabled={isModifyingTemplate}
                          >
                            AI Create
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {documentTemplates.length === 0 && !isLoadingDocuments && (
                    <div className="no-documents">
                      No documents found for {client?.First_Legal_Name} {client?.Last_Legal_Name}
                      <br />
                      <small>No documents found for this client</small>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Save Document Modal */}
        {showSaveModal && (
          <div className="save-document-modal">
            <div className="save-document-modal__header">
              <h3>{existingDocumentId ? 'Edit Document' : 'Save Document'}</h3>
              <button 
                onClick={() => {
                  setShowSaveModal(false);
                  setExistingDocumentId(null);
                }}
                className="save-document-modal__close"
              >
                ×
              </button>
            </div>
            
            <div className="save-document-modal__form">
              <div className="form-group">
                <label htmlFor="documentTitle">Document Title *</label>
                <input
                  type="text"
                  id="documentTitle"
                  value={saveDocumentData.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    let autoCategory = saveDocumentData.category;
                    
                    // Auto-detect category based on title keywords
                    const titleLower = title.toLowerCase();
                    if (titleLower.includes('management') && titleLower.includes('only')) {
                      autoCategory = 'management';
                    } else if (titleLower.includes('all') && (titleLower.includes('inclusive') || titleLower.includes('in'))) {
                      autoCategory = 'all';
                    } else if (titleLower.includes('recover') || titleLower.includes('recovery')) {
                      autoCategory = 'recover';
                    } else if (titleLower.includes('standard')) {
                      autoCategory = 'standard';
                    }
                    
                    setSaveDocumentData(prev => ({
                      ...prev, 
                      title: title,
                      category: autoCategory
                    }));
                  }}
                  placeholder="Enter document title"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="documentCategory">Category</label>
                <select
                  id="documentCategory"
                  value={saveDocumentData.category}
                  onChange={(e) => setSaveDocumentData(prev => ({...prev, category: e.target.value}))}
                >
                  <option value="general">General</option>
                  <option value="standard">Standard Contract</option>
                  <option value="custom">Custom</option>
                  <option value="template">Template</option>
                  <option value="client">Client</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="documentAssignment">Assignment</label>
                <select
                  id="documentAssignment"
                  value={saveDocumentData.assignedTo}
                  onChange={(e) => setSaveDocumentData(prev => ({...prev, assignedTo: e.target.value}))}
                >
                  <option value="template">Template (General Use)</option>
                  <option value="employee">Employee Document</option>
                  <option value="client">Client Document</option>
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="documentDescription">Description</label>
                <textarea
                  id="documentDescription"
                  value={saveDocumentData.description}
                  onChange={(e) => setSaveDocumentData(prev => ({...prev, description: e.target.value}))}
                  placeholder="Brief description of this document"
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="documentTags">Additional Tags (comma-separated)</label>
                <input
                  type="text"
                  id="documentTags"
                  value={saveDocumentData.tags}
                  onChange={(e) => setSaveDocumentData(prev => ({...prev, tags: e.target.value}))}
                  placeholder="e.g., contract, standard, influencer"
                />
                <small className="form-help">
                  Note: Assignment tags (employee/client/template) will be added automatically
                </small>
              </div>

              {!existingDocumentId && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={saveDocumentData.replaceExisting}
                      onChange={(e) => setSaveDocumentData(prev => ({...prev, replaceExisting: e.target.checked}))}
                    />
                    Replace existing document with same title
                  </label>
                  <small className="form-help">
                    If checked, will update any existing document with the same title instead of creating a duplicate
                  </small>
                </div>
              )}
              
              <div className="save-document-modal__actions">
                <button 
                  onClick={() => {
                    setShowSaveModal(false);
                    setExistingDocumentId(null);
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSaveReusableDocument}
                  className="btn btn-primary"
                  disabled={!saveDocumentData.title || isUpdatingDocument}
                >
                  {isUpdatingDocument ? 'Saving...' : 
                   existingDocumentId ? 'Update Document' : 'Save Document'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* Revision Modal */}
        {showRevisionModal && (
          <div className="revision-modal">
            <div className="revision-modal__header">
              <h3>Send for Revisions</h3>
              <button 
                onClick={() => setShowRevisionModal(false)}
                className="revision-modal__close"
              >
                ×
              </button>
            </div>
            
            <div className="revision-modal__form">
              <div className="form-group">
                <label htmlFor="revisionTitle">Review Title *</label>
                <input
                  type="text"
                  id="revisionTitle"
                  value={revisionData.title}
                  onChange={(e) => setRevisionData(prev => ({...prev, title: e.target.value}))}
                  placeholder="Enter review title"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="reviewerName">Reviewer Name *</label>
                <input
                  type="text"
                  id="reviewerName"
                  value={revisionData.reviewerName}
                  onChange={(e) => setRevisionData(prev => ({...prev, reviewerName: e.target.value}))}
                  placeholder="Enter reviewer's full name"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="reviewerEmail">Reviewer Email *</label>
                <input
                  type="email"
                  id="reviewerEmail"
                  value={revisionData.reviewerEmail}
                  onChange={(e) => setRevisionData(prev => ({...prev, reviewerEmail: e.target.value}))}
                  placeholder="Enter reviewer's email address"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="revisionMessage">Message</label>
                <textarea
                  id="revisionMessage"
                  value={revisionData.message}
                  onChange={(e) => setRevisionData(prev => ({...prev, message: e.target.value}))}
                  placeholder="Add a message for the reviewer..."
                  rows={4}
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="revisionDueDate">Due Date</label>
                <input
                  type="date"
                  id="revisionDueDate"
                  value={revisionData.dueDate}
                  onChange={(e) => setRevisionData(prev => ({...prev, dueDate: e.target.value}))}
                />
              </div>
              
              <div className="revision-modal__actions">
                <button 
                  onClick={() => setShowRevisionModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleCreateRevision}
                  className="btn btn-primary"
                  disabled={!revisionData.reviewerEmail || !revisionData.reviewerName || !revisionData.title || isSendingRevision}
                >
                  {isSendingRevision ? 'Sending...' : 'Send for Review'}
                </button>
              </div>
            </div>
          </div>
        )}
        
        {/* AI Modify Template Modal */}
        {showAIModifyModal && (
          <div className="ai-modify-template-modal">
            <div className="ai-modify-template-modal__header">
              <h3>🤖 Create with AI</h3>
              <button 
                onClick={() => setShowAIModifyModal(false)}
                className="ai-modify-template-modal__close"
              >
                ×
              </button>
            </div>
            
            <div className="ai-modify-template-modal__form">
              <div className="form-group">
                <label htmlFor="aiModificationPrompt">Template Modification Instructions *</label>
                <textarea
                  id="aiModificationPrompt"
                  value={aiModificationPrompt}
                  onChange={(e) => setAIModificationPrompt(e.target.value)}
                  placeholder="Example: Change the payment terms to $5000 instead of the current amount. Update the deliverables to include 3 Instagram posts and 2 TikTok videos. Set the campaign duration to 30 days."
                  rows={6}
                  required
                />
                <small className="form-help">
                  💡 The AI will load your existing template and modify it according to your instructions. Be specific about what changes you want made.
                </small>
              </div>
              
              <div className="ai-modify-template-modal__actions">
                <button 
                  onClick={() => setShowAIModifyModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleApplyAIModification}
                  className="btn btn-primary"
                  disabled={!aiModificationPrompt.trim() || isModifyingTemplate}
                >
                  {isModifyingTemplate ? '🤖 Creating Document...' : '✨ Create Document'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Split Info Modal */}
        {showSplitInfoModal && (
          <div className="split-modal-overlay">
            <div className="split-modal">
              <div className="split-modal-header">
                <h3>Payment Split Information</h3>
                <button onClick={() => setShowSplitInfoModal(false)}>&times;</button>
              </div>
              <div className="split-modal-content">
                <p>Please enter the payment split information before sending the contract.</p>
                <p className="split-modal-note" style={{ marginBottom: '20px', color: '#666', fontSize: '0.9em' }}>
                  Note: The total of client payout and company net must be less than 100% to allow for referral fees.
                  The remaining percentage will be allocated to referrals.
                </p>
                
                <div className="split-modal-field">
                  <label>Client Payout Percentage</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={splitInfo.client_payout_percent}
                    onChange={(e) => setSplitInfo(prev => ({
                      ...prev,
                      client_payout_percent: e.target.value
                    }))}
                    placeholder="Enter percentage (0-100)"
                  />
                  <small>What percentage of gross does the client receive?</small>
                </div>

                <div className="split-modal-field">
                  <label>Company Net Percentage</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={splitInfo.company_net_percent}
                    onChange={(e) => setSplitInfo(prev => ({
                      ...prev,
                      company_net_percent: e.target.value
                    }))}
                    placeholder="Enter percentage (0-100)"
                  />
                  <small>What percentage does the company keep after client payout AND referral fees?</small>
                </div>
              </div>
              <div className="split-modal-actions">
                <button 
                  className="split-modal-cancel"
                  onClick={() => setShowSplitInfoModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="split-modal-add"
                  onClick={handleSaveSplitInfo}
                  disabled={isSavingSplits}
                >
                  {isSavingSplits ? 'Saving...' : 'Save and Send Contract'}
                </button>
              </div>
            </div>
          </div>
        )}
        
      </div>
    </div>
  );
};

export default ContractModal; 